/**
 * «Мой голос» — build a Suno voice from a live recording, then sing with it.
 *
 * Three measured facts shape this file; none of them are visible in the API docs:
 *
 *  1. The verification phrase dies in ~100 seconds, and the server that issued it
 *     keeps reporting `wait_validating` long after. So the countdown here is the
 *     only honest clock on the page: when it runs out we re-arm the whole step
 *     instead of letting someone record into a dead task.
 *
 *  2. Failure is FREE. Nothing in this flow spends tokens, so every refusal ends
 *     in «try again» wired to a button, never in a dead end — and we say so, or
 *     people assume a botched take cost them money (the exact complaint that
 *     shaped the SVC lane's paid preview).
 *
 *  3. A quiet phone recording passes. Verified end-to-end with takes peaking at
 *     −19.8 dB, so the level meter here warns rather than blocks: refusing a
 *     recording Suno would have accepted is the worse error.
 */
(function () {
    'use strict';

    var root = document.getElementById('svo');
    if (!root) return;

    var U = {
        info:    root.dataset.infoUrl,
        start:   root.dataset.startUrl,
        phrase:  root.dataset.phraseUrl,
        rephrase: root.dataset.rephraseUrl,
        confirm: root.dataset.confirmUrl,
        state:   root.dataset.stateUrl,
        del:     root.dataset.deleteUrl,
        upload:  root.dataset.uploadUrl,
        action:  root.dataset.actionUrl,
        generate: root.dataset.generateUrl,
        persona: root.dataset.personaUrl,
        status:  root.dataset.statusUrl,
        cert:    root.dataset.certUrl,
        certMatch: root.dataset.certMatchUrl,
        bootstrap: root.dataset.bootstrapUrl,
        login:   root.dataset.loginUrl,
        song:    root.dataset.songUrl,
        lyricsBase: root.dataset.lyricsBase,
        lyricsGen: root.dataset.lyricsGenUrl,
        profile: root.dataset.profileUrl,
        history: root.dataset.historyUrl,
        hideGen: root.dataset.hideUrl,
        pipeline: root.dataset.pipelineUrl,
        goTo:    (function () { try { return JSON.parse(root.dataset.goto || '{}'); } catch (e) { return {}; } })()
    };
    var T = JSON.parse(root.dataset.i18n || '{}');
    var $ = function (id) { return document.getElementById(id); };
    /* Привязка события — ТОТАЛЬНАЯ операция.
     *
     * Инициализация вешает 17 обработчиков подряд, и раньше часть шла через
     * `if ($('x')) $('x').addEventListener(...)`, а часть — напрямую. Первый
     * же отсутствующий элемент из «напрямую» уносил ВЕСЬ остаток инициализации:
     * ни одной последующей привязки, ни loadInfo() в конце — тул выглядел
     * загрузившимся и не делал ничего (Sentry FV, /voice-cover, Yandex 26.6.0:
     * `$('svoSing').addEventListener` на null).
     *
     * Элемент пропадает не только «по ошибке вёрстки»: событие FV пришло с
     * релиза, в разметке которого этой кнопки ЕЩЁ НЕ БЫЛО, — то есть страница
     * из кэша встретилась с более новым js. Такое расхождение неизбежно, и
     * правильный ответ на него — не падать целиком, а недосчитаться одного
     * обработчика и сказать об этом в телеметрию.
     *
     * Разница со «сплошными if» — не в объёме кода, а в том, что забыть
     * проверку здесь НЕЛЬЗЯ: другого способа привязаться в модуле нет. */
    var missingIds = [];
    function on(id, evt, fn, opts) {
        var el = typeof id === 'string' ? $(id) : id;
        if (!el) {
            if (typeof id === 'string' && missingIds.indexOf(id) < 0) missingIds.push(id);
            return null;
        }
        el.addEventListener(evt, fn, opts);
        return el;
    }
    /* Один отчёт на инициализацию, а не по одному на элемент: расхождение
     * версий выбивает сразу пачку, и 17 событий об одном и том же — шум. */
    function reportMissing(where) {
        if (!missingIds.length) return;
        try {
            window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'svo_dom_missing', where: where, ids: missingIds.join(',') });
        } catch (_) {}
        try { console.warn('[suno-voice] нет элементов:', missingIds.join(', ')); } catch (_) {}
    }

    var state = {
        voices: [],
        picked: 0,
        task: null,
        phrase: '',
        phraseAt: 0,       // когда фраза показана — точка отсчёта окна
        deadline: 0,
        sample: null,
        limits: { min: 15, max: 240 },
        busy: false,
        /* ЧТО СЕЙЧАС СЧИТАЕТСЯ, а не «идёт ли запрос».
         *
         * `busy` жил ровно столько, сколько длился POST: обе кнопки гасли на
         * секунду сабмита и оживали, как только сервер ответил 202 — то есть на
         * все 2–5 минут генерации. Второе нажатие резервировало ЕЩЁ 4000
         * токенов, кошелёк отвечал 402, и человек читал «пополните счёт»,
         * стоя на полном кошельке.
         *
         * Замер за 90 дней: на /voice-cover стена сработала 38 раз, и в 10
         * случаях (26%) токены держала его же генерация; на /song-generator,
         * где ровно этот замок есть (`st.activeJobId`), — 2 из 688 (0.3%).
         * Разница в 90 раз и есть цена отсутствующего замка. Возврат #24
         * (29.08) — последний экран перед «верните деньги».
         *
         * ⚠️ Снимать ТОЛЬКО в терминальных ветках watchGeneration: done,
         * failed, таймаут ожидания и отказ опроса. Иначе кнопка запрётся
         * навсегда, а это хуже лишнего резерва. */
        activeJobId: null,
        /* Сколько раз ПОДРЯД Suno не узнал голос во фразе (`mismatch`).
         *
         * Заявка 24.09.2026 (Lowina, Премиум куплен ради этого тула): 22 таких
         * отказа за два вечера, и после каждого страница предлагала одно и то
         * же — новую фразу к тому же пению, тем же способом. Замер того же дня
         * на живом Suno (синтетика, 5 раундов): пение в образце и РЕЧЬ во фразе —
         * 11 из 17, и у одних голосов не проходит никогда (ru_eduard 0/3); фраза
         * НАПЕВНО на простой мотив — 7 из 7. Поэтому первый отказ меняет СОВЕТ
         * (пропойте фразу напевно), а второй подряд предлагает сделать обе записи
         * одним способом — чтением (речь/речь 8 из 9, неуспех — зависание самого
         * Suno), — вместо третьего круга того же. */
        voiceRefusals: 0,
        // Образец записывают чтением вслух (после двух отказов подряд) — тогда и
        // фразу читают. Живёт до успеха: закрыть и открыть мастер — не повод
        // вернуть человека в режим, который у него не проходит.
        readMode: false
    };

    /* ── Кто нажимает — знает СЕРВЕР, и знает при рендере ────────────────
     *
     * До 09.09.2026 `state.auth`/`state.verified` заполнял только ответ
     * `voice/info`, а до его прихода (или после его провала — `.catch` был
     * пустым) оба были undefined, и кнопка «Записать голос» отвечала на это
     * `location.href = login`. Логин же для залогиненного — 302 в профиль.
     * То есть Премиум-пользователь, у которого JSON шёл дольше, чем он ждал,
     * или не дошёл вовсе, каждым нажатием получал СВОЙ ПРОФИЛЬ вместо мастера.
     * Заявка S.V. 08.09.2026, 1249 ₽, восемь нажатий, «ни чего не работает»:
     * страница у него грузилась 48 с (TTFB на телефоне), и в каждой из трёх
     * сессий JSON так и не был обработан, хотя сервер отвечал 200 за 30 мс.
     * Nginx за 14 дней: 19 IP-дней с тем же 302 из voice-cover в /login.
     *
     * Ответ: залогинен ли читатель, страница знает В МОМЕНТ РЕНДЕРА
     * (`data-authed`/`data-verified` на корне — так уже делает соседний блок
     * vco), и это первое слово. JSON может его ПОДТВЕРДИТЬ или явно ОТМЕНИТЬ
     * (сессия истекла — сервер сказал auth:false), но не может отменить своим
     * ОТСУТСТВИЕМ. Гостевая страница из кэша несёт «0» — для гостя это правда;
     * залогиненный получает свежий HTML (bypass по куке) и «1». */
    function flag(v) { return v === '1' ? true : (v === '0' ? false : undefined); }
    /* ⚠️ Но и разметка может быть НЕ с сервера. Service Worker на медленной
     * сети (NetworkFirst, таймаут 4 с) отдаёт копию страницы из кэша, и у
     * вошедшего это ГОСТЕВАЯ копия с `data-authed="0"` — от визита до входа
     * (личный ответ вытесняет её лишь когда сам доезжает). Поэтому первое
     * слово — кука `timbrica_auth` (`window.__signedIn`): её ставит каждый
     * путь входа и снимает выход, и вместе с HTML она не протухает. Когда кука
     * и разметка расходятся, устарел весь HTML: «вошёл» берём у куки, а
     * «подтверждена ли почта» — не знаем и спрашиваем сервер, а не гадаем. */
    (function () {
        var html = flag(root.dataset.authed);
        var cookie = (typeof window.__signedIn === 'function') ? window.__signedIn() : null;
        if (cookie === true && html !== true) {
            state.auth = true;
            state.verified = undefined;
            state.staleHtml = true;
            try {
                window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'svo_stale_html', html: html === undefined ? 'none' : '0' });
            } catch (_) {}
        } else {
            state.auth = html;
            state.verified = flag(root.dataset.verified);
        }
    })();
    /* «Не знаем» — это два случая: HTML старого релиза без атрибута
     * (auth undefined) и устаревшая копия страницы у вошедшего (auth true,
     * verified undefined). В обоих кнопка спрашивает сервер, а не гадает. */
    function gateUnknown() {
        return state.auth === undefined || (state.auth === true && state.verified === undefined);
    }

    // ── plumbing ────────────────────────────────────────────────────────────
    function csrf() {
        var m = document.querySelector('meta[name="csrf-token"]');
        return m ? m.getAttribute('content') : '';
    }

    function post(url, body) {
        var opts = { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf(), 'Accept': 'application/json' }, body: body };
        if (!(body instanceof FormData)) {
            opts.headers['Content-Type'] = 'application/json';
            opts.body = JSON.stringify(body);
        }
        return fetch(url, opts).then(function (r) {
            return r.json().catch(function () { return {}; }).then(function (j) {
                j.__status = r.status;
                return j;
            });
        });
    }

    function get(url) {
        return fetch(url, { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
    }

    // ── Телеметрия воронки ───────────────────────────────────────────────────
    // Главный незамеренный вопрос этого лейна: убивает ли людей стосекундное окно
    // фразы. Пока данных нет, любое решение о нём — вкусовщина. `tool_stage` и
    // `tool_funnel` уже в ALLOWED_TYPES, так что события не отбрасываются молча
    // (та ловушка стоила нам месяцев слепоты по поиску).
    function stage(name) { try { if (window.__stage) window.__stage(name); } catch (e) {} }
    function funnel(name, data) { try { if (window.__aeFunnel) window.__aeFunnel(name, data || {}); } catch (e) {} }

    function say(msg, kind) {
        if (window.__toast) window.__toast(msg, kind || 'info');
    }

    /* ── Ящик отказа: касса и «вопрос о короткой песне» ────────────────────
     *
     * Тост уходит через секунды и не несёт двери. Замер 11.09.2026 (#6889):
     * человек десять раз подряд получил тостом «Текста хватит примерно на
     * 0:51…» — а это ВОПРОС сервера (AiMusicController: `lyrics_too_short`
     * ждёт `short_ok`), и на /song-generator у него есть кнопка «Создать как
     * есть». Здесь её не было — вопрос читался как стена. Точно так же 402
     * приходил словами «пополните счёт в профиле» без ссылки, хотя ссылка
     * «Пополнить» на /pricing#tokens перехватывается token-wall.js и продаёт
     * пакет на месте (`data-tmb-short` — нехватка под ЭТУ песню).
     *
     * `opts.topup` — показать дверь в кассу, `opts.short` — нехватка в токенах,
     * `opts.ask` — это вопрос, показать «Создать как есть». Без ящика в DOM
     * (страница без Suno-лейна) — прежний тост, чтобы отказ всё равно дошёл. */
    var errAgain = null;   // что повторить по «Создать как есть»
    function showErr(msg, opts) {
        opts = opts || {};
        var box = $('svoErr'), text = $('svoErrText'), topup = $('svoErrTopup'), anyway = $('svoErrAnyway'), lane = $('svoErrLane');
        if (!box || !text) { say(msg, opts.ask ? 'warning' : 'error'); return; }
        text.textContent = msg || '';
        box.classList.toggle('is-ask', !!opts.ask);
        if (topup) {
            topup.hidden = !opts.topup;
            if (opts.short > 0) topup.setAttribute('data-tmb-short', String(opts.short));
            else topup.removeAttribute('data-tmb-short');
        }
        if (anyway) anyway.hidden = !opts.ask;
        if (lane) lane.hidden = !opts.lane;
        errAgain = opts.ask ? (opts.again || null) : null;
        box.hidden = false;
        try { box.scrollIntoView({ block: 'nearest' }); } catch (e) {}
    }
    function hideErr() {
        var box = $('svoErr');
        if (box) box.hidden = true;
        errAgain = null;
    }
    on('svoErrLane', 'click', function () {
        funnel('refusal_lane_switch', { had_file: laneFile ? 1 : 0 });
        showTab('song');
    });
    on('svoErrAnyway', 'click', function () {
        var again = errAgain;
        hideErr();
        if (typeof again === 'function') again();
    });

    /* ── Баланс рядом с ценой ───────────────────────────────────────────────
     *
     * Тот же bootstrap, что читает /song-generator (одна касса, одно число).
     * Цена на странице стояла всегда, а остаток — нет, поэтому «хватит ли»
     * узнавалось только отказом после нажатия: 31 человек за 30 дней увидел
     * «нужно 4 500, на счету 2 000» на этой странице. Теперь нехватка видна
     * ДО нажатия, с дверью в кассу — и обновляется после каждой генерации. */
    state.balance = null;
    state.cost = 0;
    function renderBalance() {
        var el = $('svoBalance');
        if (!el) return;
        if (typeof state.balance !== 'number') { el.hidden = true; return; }
        el.textContent = (T.balance || ':n').replace(':n', String(state.balance).replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
        var short = state.cost > 0 ? Math.max(0, state.cost - state.balance) : 0;
        el.classList.toggle('is-short', short > 0);
        el.hidden = false;
        if (short > 0) {
            showErr((T.short_fmt || '').replace(':n', String(short)), { topup: true, short: short });
        }
    }
    function loadBalance() {
        if (!U.bootstrap || !state.auth || !state.verified) return Promise.resolve();
        return get(U.bootstrap).then(function (j) {
            if (!j || typeof j.balance !== 'number') return;
            state.balance = j.balance;
            if (j.costs && typeof j.costs.song === 'number') state.cost = j.costs.song;
            renderBalance();
        }).catch(function () {});
    }
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') loadBalance();
    });

    /** Suno's refusal codes → the one sentence that is actually true for the user.
     *
     * ⚠️ Незнакомый код молча превращался в «Не получилось. Попробуйте ещё раз
     * — попытка бесплатна». Для кода `auth` это была прямая неправда: попытка
     * не могла удаться НИКОГДА, а человек повторял. 88 таких показов у 20
     * человек за сутки. Поэтому auth/verify отвечают своими фразами. */
    function reasonText(code, data) {
        if (code === 'auth') return T.need_auth || T.err_failed || '';
        if (code === 'needs_verification') return T.need_verify || T.err_failed || '';
        /* ⚠️ Тот же капкан, что и у `auth`, на соседнем коде: у кончившихся
         * токенов перевода не было, и человек читал «попытка бесплатна» —
         * приглашение жать кнопку, которая не может сработать. Замер 22.08
         * (user 5625, Премиум, кошелёк 1800 при цене 4000): пять нажатий подряд,
         * пять раз это сообщение. Числа подставляем, иначе «не хватает» не
         * говорит, сколько именно не хватает. */
        if (code === 'insufficient_tokens') {
            /* Две РАЗНЫЕ преграды под одним кодом, и до 29.08 обе читались как
             * «пополните счёт». `reason:'held'` значит, что деньги у человека
             * есть и держит их его же генерация — счёт тут ни при чём, и совет
             * платить снова отправляет платить дважды за то, что уже куплено.
             * Ветку выбирает СЕРВЕР (TokenRefusal), страница только озвучивает. */
            if (data && data.reason === 'held' && T.err_tokens_held) {
                return T.err_tokens_held.replace(':held', String((data && data.held) || 0));
            }
            /* С 28.09 сервер называет и другие преграды — грант, который
             * источник не берёт, и дневной потолок. Своих строк под них у
             * страницы нет, а «не хватает: нужно N, на счету M» при M ≥ N —
             * бессмыслица; слова сервера (TokenRefusal) точнее. */
            if (data && data.reason && data.reason !== 'empty' && data.msg) {
                return data.msg;
            }
            if (T.err_insufficient_tokens) {
                return T.err_insufficient_tokens
                    .replace(':need', String((data && data.need) || ''))
                    .replace(':balance', String((data && data.balance) || 0));
            }
        }
        return T['err_' + code] || T.err_failed || '';
    }

    /**
     * Слова для отказа.
     *
     * Обычно сообщение сервера точнее нашего — оно знает, ЧТО именно случилось.
     * Исключение одно: у `insufficient_tokens` на этой странице есть своя пара
     * строк под две преграды (пустой кошелёк / занятые токены), и она конкретнее
     * общей кассовой фразы. Поэтому для этого кода первым спрашиваем страницу,
     * для всех остальных — сервер.
     */
    function failMsg(j) {
        if (j && j.error === 'insufficient_tokens') {
            return reasonText(j.error, j) || j.msg || T.err_failed || '';
        }
        return (j && j.msg) || reasonText(j && j.error, j) || T.err_failed || '';
    }

    /**
     * Запереть/отпереть шаг. Раньше шаги 2 и 3 просто не существовали в DOM до
     * своей очереди, и страница отвечала на «что дальше» пустотой: человек видел
     * «1» и не находил «2». Заперто — значит видно целиком, но не трогается.
     * `inert` обязателен: одного `pointer-events:none` мало — погашенная форма
     * всё равно ловит Tab и читается скринридером как рабочая.
     */
    /* Замок шага — про «нечем петь», а не про «сюда нельзя ходить».
     *
     * `inert` на всей секции гасил и ряд вкладок, а третья вкладка («кавер на
     * свою песню») работает НАШИМ движком и голоса Suno не требует вовсе.
     * Замер 09.09: за 30 дней её открыли 0 раз при 5706 сессиях, а
     * предупреждение «вы загрузили готовую песню» — тот самый экран, ради
     * которого режим и сделан, — не показалось НИ РАЗУ с 25.08 (до 20.08 его
     * видели 15–27 человек в день). Iallayeva 03.09 описала этот режим слово в
     * слово («записываю голос, вставляю песню, идёт переозвучка») и услышала
     * «это в другом инструменте»: вкладка была за замком, поставленным не для
     * неё.
     *
     * ⚠️ Снять `inert` у потомка НЕЛЬЗЯ — по спецификации он наследуется и
     * изнутри не отменяется. Поэтому запираются ЧАСТИ (`data-lock-part`), а не
     * секция; разметка приходит с сервера уже в этом виде. */
    function lock(id, on) {
        var el = $(id);
        if (!el) return;
        el.hidden = false;
        el.classList.toggle('is-locked', !!on);
        var parts = el.querySelectorAll('[data-lock-part]');
        if (!parts.length) {
            if (on) el.setAttribute('inert', ''); else el.removeAttribute('inert');
            return;
        }
        el.removeAttribute('inert');
        [].forEach.call(parts, function (part) {
            if (on) part.setAttribute('inert', ''); else part.removeAttribute('inert');
        });
    }

    // ── voices list ─────────────────────────────────────────────────────────
    /** Голос, который провайдер больше не признаёт (сервер считает это в
     *  `VoiceLiveness`, страница только показывает). */
    function isDead(v) { return !!(v && v.expired); }
    /** Голос человека: записанный (`voice`) или сохранённый из песни, спетой им
     *  (`kept`, VoiceKeep на сервере) — второй не умирает у Suno и выбирается
     *  здесь наравне с первым. Чужое «звучание трека» (`style`) сюда не идёт. */
    function isOwnVoice(v) { return !!v && (v.kind === 'voice' || v.kind === 'kept'); }

    function renderVoices() {
        var box = $('svoVoices');
        var mine = state.voices.filter(isOwnVoice);
        var live = mine.filter(function (v) { return !isDead(v); });
        box.innerHTML = '';

        if (!mine.length) {
            /* «Где в Тимбрике мне найти СВОЙ звук?» (#1690). Пустой список молчал,
               и место, где голос появится, приходилось искать по всему сайту —
               притом что оно прямо здесь. Строка стоит ДО записи, а не после. */
            if (T.voices_empty) {
                var empty = document.createElement('p');
                empty.className = 'svo-voices-empty';
                empty.textContent = T.voices_empty;
                box.appendChild(empty);
            }
            lock('svoUse', true);
            lock('svoResult', true);
            // Форма записи открыта СРАЗУ. Кнопка «＋ создать голос» перед пустым
            // инструментом — лишний клик перед единственным возможным действием:
            // у человека без голосов другого пути отсюда нет.
            //
            // ⚠️ НО не тому, кто ею всё равно не воспользуется. Заслон по
            // аккаунту стоял на кнопке «Записать голос», а этот автооткрыв его
            // МИНОВАЛ: гость получал форму записи, пел, и только при отправке
            // упирался в 401. Замер 21–22.08: 89 из 100 отправок образца —
            // именно 401. Тому, кто не вошёл, показываем кнопку и объяснение;
            // мастер откроется, когда войдёт.
            if ($('svoWiz').hidden && !state.busy && state.auth === true && state.verified === true) openWizard(true);
            return;
        }

        /* Кого мы поём — решается ДО отрисовки. Раньше `pickFirstIfNone()`
         * стоял ПОСЛЕ цикла, и первый экран показывал ряд вообще без
         * выделения: голос был выбран (шаг 2 назван его именем), а на
         * таблетках это не отражалось до первого клика. */
        if (state.picked && !live.some(function (v) { return v.id === state.picked; })) state.picked = 0;
        pickFirstIfNone();

        mine.forEach(function (v) {
            var pill = document.createElement('div');
            pill.className = 'svo-pill' + (v.id === state.picked ? ' is-on' : '') + (isDead(v) ? ' is-dead' : '');

            var pick = document.createElement('button');
            pick.type = 'button';
            pick.className = 'svo-pill-pick';
            pick.textContent = '🎙 ' + v.name + (v.kind === 'kept' && T.voice_kept ? ' · ' + T.voice_kept : '');
            if (isDead(v)) {
                /* Мёртвый голос виден, но не выбирается: пропасть он не может —
                 * человек его завёл. Сервер отдаёт про него `expired` с 30.08
                 * (`VoiceLiveness`), а ЭТА страница поле не читала вовсе —
                 * вердикт был, до экрана не доезжал, и мёртвый голос стоял в
                 * ряду с живыми. Замер 09.09 на 24 заведомо мёртвых голосах:
                 * провайдер называет мёртвыми 20 из 24 и ни разу не назвал
                 * мёртвым живой (калибровка выдуманным id: «не знаю», не «жив»).
                 * Тот же вид у соседа по движку — `sg-persona-pill.is-expired`. */
                pick.disabled = true;
                pick.title = T.voice_dead || '';
                pick.setAttribute('aria-disabled', 'true');
            } else {
                pick.addEventListener('click', function () { state.picked = v.id; renderVoices(); });
            }

            var del = document.createElement('button');
            del.type = 'button';
            del.className = 'svo-pill-x';
            del.setAttribute('aria-label', T.delete_voice || 'Delete');
            del.textContent = '×';
            del.addEventListener('click', function () {
                if (!window.confirm(T.confirm_delete || '')) return;
                post(U.del, { id: v.id }).then(function (j) {
                    if (j.ok) {
                        state.voices = j.voices || [];
                        if (state.picked === v.id) state.picked = 0;
                        pickFirstIfNone();
                        renderVoices();
                    }
                });
            });

            pill.appendChild(pick);
            /* «Сохранить навсегда»: записанный голос живёт у Suno около суток, а
             * персона, снятая с песни, уже спетой им, не умирает (VoiceKeep; замер
             * 23.09 — голос в новой песне держится, 0.75–0.89 против 0.58 без
             * неё). Кнопка — у каждого записанного голоса, из которого есть
             * песня; у умершего это единственный путь без новой записи. */
            if (v.keep && U.persona) {
                var keep = document.createElement('button');
                keep.type = 'button';
                keep.className = 'svo-pill-keep';
                keep.textContent = T.keep_btn || 'Keep';
                if (v.keep.title && T.keep_from) keep.title = T.keep_from.replace(':title', v.keep.title);
                keep.addEventListener('click', function () {
                    keep.disabled = true;
                    funnel('voice_keep_click', { dead: isDead(v) ? 1 : 0 });
                    post(U.persona, { parent_id: v.keep.id, source_variant: v.keep.n, name: '' }).then(function (j) {
                        if (j.ok && j.persona) {
                            state.picked = j.persona.id;
                            if (window.__toast) window.__toast((T.kept_done || '').replace(':name', j.persona.name), 'success');
                            loadInfo();
                        } else {
                            keep.disabled = false;
                            if (window.__toast) window.__toast(j.msg || T.keep_err || '', 'error');
                        }
                    }).catch(function () { keep.disabled = false; if (window.__toast) window.__toast(T.keep_err || '', 'error'); });
                });
                pill.appendChild(keep);
            }
            pill.appendChild(del);
            box.appendChild(pill);
        });

        // Все голоса кончились — это не «нет голосов» (мастер сам не откроется,
        // человек уже проходил его), а «нужно записать заново», и так и сказано.
        var deadNote = $('svoDeadNote');
        if (deadNote) {
            deadNote.textContent = live.length ? '' : (T.voices_dead_all || '');
            deadNote.hidden = !!live.length;
        }
        if (!live.length) {
            lock('svoUse', true);
            lock('svoResult', true);
            return;
        }

        railForSavedVoice();
        lock('svoUse', false);
        syncSing();
        var chosen = live.filter(function (v) { return v.id === state.picked; })[0];
        $('svoUseName').textContent = chosen ? chosen.name : '';
        if (U.song) $('svoToSong').href = U.song + (U.song.indexOf('?') < 0 ? '?' : '&') + 'voice=' + state.picked;
    }

    function pickFirstIfNone() {
        var live = state.voices.filter(function (v) { return isOwnVoice(v) && !isDead(v); });
        if (!state.picked && live.length) state.picked = live[0].id;
    }

    /* Причина + ДЕЙСТВИЕ: одна фраза «войдите в аккаунт» без ссылки
     * оставляет человека искать вход самому, а он стоит перед кнопкой,
     * которая только что ничего не сделала. Рисуется от того, что известно
     * СЕЙЧАС: при старте — от атрибутов корня, после info — от ответа. */
    function applyGate(auth, verified) {
        state.auth = auth;
        state.verified = verified;
        var note = $('svoNote');
        if (!note) return;
        note.textContent = '';
        if (auth === false || (auth === true && verified === false)) {
            note.appendChild(document.createTextNode(
                (auth ? (T.need_verify || '') : (T.need_auth || '')) + ' '));
            var to = auth ? U.profile : U.login;
            if (to) {
                var link = document.createElement('a');
                link.href = to;
                link.textContent = auth ? (T.profile || '') : (T.login || '');
                if (link.textContent) note.appendChild(link);
            }
            note.hidden = false;
        } else {
            note.hidden = true;
        }
    }

    var infoRetried = false;
    function loadInfo() {
        return get(U.info).then(function (j) {
            if (!j.enabled) { root.hidden = true; return; }

            // The tool is always visible. What changes with sign-in state is what
            // pressing the button DOES, and one quiet line saying why — never a
            // wall in front of someone still deciding whether to try it.
            // Это ЯВНЫЙ ответ сервера — он сильнее атрибута: истёкшая сессия
            // здесь честно опускает «1» до false.
            applyGate(!!j.auth, !!j.verified);

            state.voices = j.voices || [];
            // Лейн не выдаёт фразы — сказать об этом ДО записи, а не после.
            // Значение приходит из кэша (пишет voice:suno-lane-health), поэтому
            // ручка остаётся дешёвой.
            var down = $('svoLaneDown');
            if (down) { down.hidden = !j.lane_down; }
            // Работает, но медленно — тоже сказать до записи, иначе долгое ожидание
            // на шаге 2 читается как зависание. Бюджеты ожидания приходят ОТСЮДА
            // же: страница и сторож лейна ждут одно и то же число секунд
            // (SunoVoiceService::PHRASE_*), и второй копии числа в JS нет.
            var slow = $('svoLaneSlow');
            if (slow) { slow.hidden = !(j.lane_slow && !j.lane_down); }
            if (j.phrase_wait_s) { WAIT_PHRASE_MS = j.phrase_wait_s * 1000; }
            if (j.phrase_slow_s) { state.phraseSlowMs = j.phrase_slow_s * 1000; }
            state.limits = { min: j.sample_min_s || 15, max: j.sample_max_s || 240 };
            // Прошлые песни показываем только тому, у кого они могут быть:
            // гостю этот запрос вернёт 401, а на экране появится пустой блок,
            // объясняющий не то.
            if (state.auth && state.verified) { loadHistory(false); loadBalance(); }
            $('svoSampleHint').textContent = (T.sample_hint || '')
                .replace(':min', String(state.limits.min))
                .replace(':max', String(Math.round(state.limits.max / 60)));
            renderVoices();
        }).catch(function (e) {
            /* Блок остаётся как отрендерен — и это теперь РАБОЧИЙ блок, потому
             * что «кто нажимает» страница знает без этого ответа. Провал не
             * молчит: до 09.09 этот catch был пустым, и класс «JSON не дошёл»
             * был невидим всем приборам, кроме silent_click, который никто не
             * читал. Одна повторная попытка — телефонная сеть, которая держала
             * HTML 48 с, вторым запросом обычно отвечает. */
            try {
                window.__aeDiagnostic && window.__aeDiagnostic({
                    issue: 'svo_info_failed', retry: infoRetried ? 1 : 0,
                    why: (e && (e.name || e.message) || 'unknown').toString().slice(0, 60),
                    authed: state.auth === undefined ? 'unknown' : (state.auth ? 1 : 0)
                });
            } catch (_) {}
            if (!infoRetried) { infoRetried = true; setTimeout(loadInfo, 4000); }
        });
    }

    // ── recorder ────────────────────────────────────────────────────────────
    // One recorder object drives both takes (the song and the phrase). It hands
    // back a Blob and nothing else; every decision about what to do with it
    // lives in the step that started it.
    function Recorder() {
        this.mic = null; this.rec = null; this.chunks = []; this.started = 0; this.timer = null;
    }

    Recorder.prototype.start = function (onTick, onLevel) {
        var self = this;
        this.mic = new window.MicManager({ locale: window.__LOCALE });
        return this.mic.start().then(function (stream) {
            self.chunks = [];
            self.rec = new MediaRecorder(stream);
            self.rec.ondataavailable = function (e) { if (e.data && e.data.size) self.chunks.push(e.data); };
            self.rec.start();
            self.started = Date.now();
            if (onLevel) self.mic.onLevel(onLevel);
            self.timer = setInterval(function () {
                if (onTick) onTick((Date.now() - self.started) / 1000);
            }, 200);
            return true;
        });
    };

    Recorder.prototype.stop = function () {
        var self = this;
        return new Promise(function (resolve) {
            if (!self.rec) { resolve(null); return; }
            clearInterval(self.timer);
            self.rec.onstop = function () {
                var blob = new Blob(self.chunks, { type: self.chunks[0] ? self.chunks[0].type : 'audio/webm' });
                try { self.mic.stop(); } catch (e) {}
                self.rec = null;
                resolve(blob);
            };
            try { self.rec.stop(); } catch (e) { resolve(null); }
        });
    };

    var recSample = new Recorder();
    var recPhrase = new Recorder();

    // ── step 1: the sung sample ─────────────────────────────────────────────
    /**
     * @param {boolean} auto  открыт сам, а не нажатием.
     *
     * ⚠️ Телеметрия только при нажатии. Автооткрытие — состояние страницы, а не
     * намерение: считать его за `voice_start` значит записать в начавшие всех,
     * кто просто открыл вкладку, и воронка перестаёт что-либо мерить.
     * ⚠️ Рельса подшагов тоже только при нажатии: рядом с постоянно видимыми
     * секциями 1–2–3 вторая нумерация 1–2–3 на первом экране читается как та же
     * самая. Внутри процесса она отвечает «где я», на входе — только шумит.
     */
    function openWizard(auto) {
        phraseGen++;
        dropPhraseTake('closed');
        forgetPhraseTake();
        state.task = null; state.phrase = ''; state.sample = null;
        clearTake();
        $('svoWiz').hidden = false;
        $('svoRail').hidden = !!auto;
        // Отменять нечего, когда мастер и есть страница: за ним нет состояния,
        // в которое можно вернуться, кроме кнопки «создать голос».
        $('svoCancel').hidden = !!auto;
        if (!auto) stage('voice_start');
        // Текст для чтения — только тому, кого мы сами перевели в режим чтения.
        var rb = $('svoReadBlock');
        if (rb) rb.hidden = !state.readMode;
        // …а совет «что спеть» в режиме чтения только сбивает.
        var tip = $('svoSampleTip');
        if (tip) tip.hidden = !!state.readMode;
        showStep(1);
        $('svoNew').hidden = true;
    }

    /* Как исполнить фразу — строка ПРЯМО НАД ней, но только когда нам есть что
     * сказать сверх общего правила шага: после отказа «голос не узнан» (значит,
     * фразу, скорее всего, прочитали после пения) и в режиме чтения. */
    function modeNote() {
        var n = $('svoModeNote');
        if (!n) return;
        var t = state.readMode ? (T.mode_read || '') : (state.voiceRefusals > 0 ? (T.mode_sing || '') : '');
        n.textContent = t;
        n.hidden = !t;
    }

    function closeWizard() {
        phraseGen++;
        dropPhraseTake('closed');
        forgetPhraseTake();
        $('svoWiz').hidden = true;
        $('svoRail').hidden = true;
        $('svoNew').hidden = false;
        // Минутная запись держит объектный URL до конца жизни вкладки, если его
        // не отпустить руками — а мастер закрывают в том числе успехом.
        clearTake();
        stopCountdown();
    }

    function showStep(n) {
        [1, 2, 3].forEach(function (i) { $('svoStep' + i).hidden = (i !== n); });
        if (n > 1) $('svoRail').hidden = false;
        rail(n);
    }

    /**
     * Keep the step rail honest. It is not decoration: a multi-step form without
     * a «where am I» costs the user the one thing they cannot recover here —
     * attention during a 100-second window.
     */
    function rail(n) {
        var box = $('svoRail');
        if (!box) return;
        [].forEach.call(box.children, function (li, i) {
            var step = i + 1;
            li.classList.toggle('is-now', step === n);
            li.classList.toggle('is-done', step < n);
        });
    }

    /** With a saved voice the first two steps are behind the user for good. */
    function railForSavedVoice() {
        var box = $('svoRail');
        if (!box) return;
        [].forEach.call(box.children, function (li, i) {
            li.classList.toggle('is-done', i < 2);
            li.classList.toggle('is-now', i === 2);
        });
    }

    on('svoNew', 'click', function () {
        // The account requirement lands HERE, at the moment of acting — not as a
        // gate the visitor meets before seeing anything.
        //
        // К логину отправляет только ЯВНОЕ «гость» (атрибут корня или ответ
        // сервера). `undefined` — это HTML старого релиза без атрибута, встретивший
        // новый js: там спрашиваем сервер ещё раз, а не гадаем «гость».
        if (gateUnknown()) {
            var btn = this; btn.disabled = true;
            loadInfo().then(function () {
                btn.disabled = false;
                if (gateUnknown()) { say(T.err_failed || '', 'warning'); return; }
                btn.click();
            });
            return;
        }
        if (state.auth === false) { location.href = U.login || '/login'; return; }
        if (state.verified === false) { say(T.err_needs_verification || T.need_verify || '', 'warning'); return; }
        openWizard();
    });
    on('svoCancel', 'click', closeWizard);
    on('svoCancel2', 'click', closeWizard);

    /* Гость не должен петь тридцать секунд в тупик.
     *
     * Замер 21–22.08 (nginx + телеметрия): из 100 отправок образца на
     * /voice/start **89 вернули 401**, а 20 человек нажали «отправить» 92 раза
     * и НИ ОДИН не увидел проверочную фразу. Записанных голосов на проде — 0.
     * Причина не в записи и не в провайдере: человек не был залогинен, но
     * узнавал об этом ТОЛЬКО после того, как спел, — и узнавал неправдой,
     * потому что кода `auth` не было в словаре причин и показывалось общее
     * «Не получилось. Попробуйте ещё раз — попытка бесплатна». Так и вышло
     * 88 повторов у 20 человек.
     *
     * Заслон стоит ПЕРЕД микрофоном и перед выбором файла — там, где отказ
     * ещё ничего не стоит. */
    function mustBeSignedIn() {
        if (state.auth === false) {
            say(T.need_auth || '', 'warning');
            if (U.login) setTimeout(function () { window.location.href = U.login; }, 900);
            funnel('voice_blocked', { why: 'auth' });
            return true;
        }
        if (state.auth === true && state.verified === false) {
            say(T.need_verify || '', 'warning');
            if (U.profile) setTimeout(function () { window.location.href = U.profile; }, 1200);
            funnel('voice_blocked', { why: 'verify' });
            return true;
        }
        return false;
    }

    /* ── дубль ЖИВЁТ, пока человек не решил ────────────────────────────────
     *
     * ⚠️⚠️ До 23.08 «Стоп» отправлял пение мгновенно и необратимо. Три следствия,
     * все замерены на проде:
     *
     *  1. Человек НИ РАЗУ не слышал ту единственную запись, которая навсегда
     *     задаёт его склонированный тембр. Заявка Gercegovinec 22.08.
     *  2. Стосекундное окно фразы открывалось в момент, когда он ещё не понял,
     *     что запись ушла.
     *  3. Любой отказ ПОСЛЕ стопа ронял блоб в сборщик мусора: несогласие,
     *     занятость, а главное — отказ самого Suno (`processing_validate_fail`,
     *     пять раз подряд у user 5240). Каждый круг стоил до 111 секунд пения
     *     заново, ровно тот же капкан, который в шаге 2 закрыли `rephrase`.
     *
     * Дубль держится ЗДЕСЬ, в переменной модуля, а не в стеке одного колбэка, и
     * уходит на сервер только по явному нажатию. Объектный URL освобождается на
     * каждой смене дубля — иначе минутная запись остаётся в памяти вкладки. */
    var pendingTake = null;
    var samplePeak = 0;

    /* Планка та же, что у фразы, и по той же причине: Suno принял дубль со
     * средним −44 дБ, поэтому «тихо» здесь — ПРЕДУПРЕЖДЕНИЕ, а не запрет.
     * Ловится только плоская линия — микрофон, не давший сигнала вообще. */
    var TAKE_FLATLINE_RMS = 0.004;
    var TAKE_QUIET_RMS    = 0.02;

    function clearTake() {
        if (pendingTake && pendingTake.url) {
            try { URL.revokeObjectURL(pendingTake.url); } catch (e) {}
        }
        pendingTake = null;
        var box = $('svoReview'), au = $('svoReviewAudio');
        if (au) { try { au.pause(); } catch (e) {} au.removeAttribute('src'); try { au.load(); } catch (e) {} }
        if (box) box.hidden = true;
        if ($('svoSampleSend')) $('svoSampleSend').hidden = true;
        if ($('svoReviewWarn')) { $('svoReviewWarn').hidden = true; $('svoReviewWarn').textContent = ''; }
    }

    /**
     * Показать дубль и ЖДАТЬ решения.
     *
     * @param {Blob}   blob
     * @param {string} name
     * @param {number|null} seconds  часы РЕКОРДЕРА, а не файла: webm от
     *        MediaRecorder не несёт Duration, и `audio.duration` в нём Infinity.
     *        Для выбранного файла остаётся null — там длительность настоящая и
     *        читается из метаданных.
     * @param {number|null} peak     пиковый RMS за дубль (только для записи).
     */
    function offerTake(blob, name, seconds, peak) {
        clearTake();
        var url = URL.createObjectURL(blob);
        pendingTake = { blob: blob, name: name, url: url, seconds: seconds };

        var au = $('svoReviewAudio');
        if (au) au.src = url;

        var meta = $('svoReviewMeta');
        function showLen(sec) {
            if (!meta) return;
            meta.textContent = sec && isFinite(sec)
                ? (T.take_len || ':t').replace(':t', fmt(sec))
                : '';
            // Слишком короткий дубль сервер отвергнет — сказать это ДО отправки
            // дешевле, чем после: отказ на сервере стоит круга ожидания.
            if (sec && isFinite(sec) && sec < state.limits.min) {
                warn((T.take_short || '').replace(':min', String(state.limits.min)));
            }
        }
        if (seconds) {
            showLen(seconds);
        } else if (au) {
            au.addEventListener('loadedmetadata', function () {
                showLen(isFinite(au.duration) ? au.duration : 0);
            }, { once: true });
        }

        if (peak !== null && peak !== undefined) {
            if (peak < TAKE_FLATLINE_RMS)      warn(T.take_flat);
            else if (peak < TAKE_QUIET_RMS)    warn(T.take_quiet);
        }

        $('svoReview').hidden = false;
        if ($('svoSampleSend')) $('svoSampleSend').hidden = false;
        funnel('voice_take_review', {
            via:  seconds ? 'rec' : 'file',
            sec:  seconds ? Math.round(seconds) : null,
            peak: (peak === null || peak === undefined) ? null : Math.round(peak * 10000) / 10000
        });
    }

    /** Одна строка предупреждения под плеером — не тост: тост уезжает, а решение принимают тут. */
    function warn(text) {
        var el = $('svoReviewWarn');
        if (!el || !text) return;
        el.textContent = text;
        el.hidden = false;
    }

    var sampleRecording = false;
    on('svoSampleRec', 'click', function () {
        if (!sampleRecording && mustBeSignedIn()) return;
        if (sampleRecording) {
            sampleRecording = false;
            $('svoSampleRec').classList.remove('is-rec');
            $('svoSampleRec').textContent = T.record || 'Record';
            var sec  = (Date.now() - recSample.started) / 1000;
            var peak = samplePeak;
            recSample.stop().then(function (blob) {
                if (blob) offerTake(blob, 'take.webm', sec, peak);
            });
            return;
        }
        samplePeak = 0;
        recSample.start(function (sec) {
            $('svoSampleTime').textContent = fmt(sec);
            // Suno's own ceiling — stop for them rather than let a long take be refused.
            if (sec >= state.limits.max) $('svoSampleRec').click();
        }, function (rms) {
            if (rms > samplePeak) samplePeak = rms;
            $('svoSampleLed').style.setProperty('--lvl', Math.min(100, Math.round(rms * 300)) + '%');
        }).then(function () {
            sampleRecording = true;
            clearTake();
            $('svoSampleRec').classList.add('is-rec');
            $('svoSampleRec').textContent = T.stop || 'Stop';
        }).catch(function () { say(T.err_mic || '', 'error'); });
    });

    on('svoSampleFile', 'change', function (e) {
        var f = e.target.files && e.target.files[0];
        if (!f) return;
        if (mustBeSignedIn()) { e.target.value = ''; return; }
        // Выбранный файл проходит тот же экран: его тоже стоит послушать —
        // «не тот файл» здесь стоит ровно столько же, сколько плохой дубль.
        offerTake(f, f.name, null, null);
    });

    on('svoSampleRedo', 'click', function () {
        funnel('voice_take_redo', { via: pendingTake && pendingTake.seconds ? 'rec' : 'file' });
        clearTake();
        $('svoSampleTime').textContent = '0:00';
        $('svoSampleLed').style.setProperty('--lvl', '0%');
        var f = $('svoSampleFile');
        if (f) f.value = '';
    });

    on('svoSampleSend', 'click', function () {
        if (!pendingTake) return;
        sendSample(pendingTake.blob, pendingTake.name);
    });

    function sendSample(blob, name) {
        if (state.busy) return;
        // Согласие спрашивается ЗДЕСЬ, до того как голос человека уйдёт
        // стороннему сервису. Раньше галочка стояла на втором шаге — то есть
        // уже ПОСЛЕ отправки образца, что неверно и по смыслу, и по существу.
        //
        // ⚠️ Оба ранних выхода выше БЕЗОПАСНЫ только потому, что дубль лежит в
        // `pendingTake`. Пока он приезжал сюда аргументом из `.then()` стопа,
        // отсюда же и терялся: «поставьте галочку» после двух минут пения
        // означало «пойте заново».
        if (!$('svoConsent').checked) { say(T.err_consent_required || '', 'warning'); return; }
        state.busy = true;
        $('svoSampleSend').disabled = true;
        $('svoSampleBusy').hidden = false;

        stage('sample_sent');
        var fd = new FormData();
        fd.append('sample', blob, name);
        post(U.start, fd).then(function (j) {
            state.busy = false;
            $('svoSampleSend').disabled = false;
            $('svoSampleBusy').hidden = true;
            // Отказ ОСТАВЛЯЕТ дубль на месте: почти все коды здесь («слишком
            // коротко», «не аудио», «наверху не приняли») человек чинит НЕ
            // перезаписью, а нажатием той же кнопки ещё раз.
            if (!j.ok) { say(reasonText(j.error, j) || (T.err_failed || ''), 'error'); return; }

            state.task = j.task;
            if (j.phrase) {
                armPhrase(j.phrase, j.ttl);
            } else {
                showStep(2);
                disarmPhrase();
                pollPhrase();
            }
        }).catch(function () {
            state.busy = false;
            $('svoSampleSend').disabled = false;
            $('svoSampleBusy').hidden = true;
            /* Сюда попадает только оборванный запрос — ответ сервера, даже отказ,
             * приходит в `.then`. Раньше и это было «Не получилось» — та же фраза,
             * что и у отказа Suno, и человек не мог отличить «сеть» от «голос»
             * (16.09, Lowina: `Failed to fetch` на 30-й секунде отправки пения). */
            say(T.err_network || T.err_failed || '', 'error');
        });
    }

    /* Всякое ожидание обязано кончаться.
     *
     * Три опроса в этом модуле крутились БЕЗ предела: фраза, голос и песня.
     * Если ответ не приходил — ни успеха, ни отказа, — человек оставался перед
     * вечным «идёт работа» и уходил. В логах это выглядит как пять-шесть
     * опросов и тишина: замер 21.08 показал ровно такую картину на шаге
     * создания голоса. Отсутствие предела — не осторожность, а молчаливый
     * отказ без объяснения.
     *
     * Пределы взяты с большим запасом к наблюдаемому времени шага, чтобы не
     * оборвать живую работу: голос — за десятки секунд, песня — за одну-три
     * минуты. Предел фразы — НЕ константа этого файла: он приходит из /voice/info
     * (phrase_wait_s) и равен бюджету сторожа лейна. До 02.09 здесь стояли свои
     * 90 с, снятые с провайдера, который выдавал фразу за 10–15 с; к сентябрю
     * он выдавал её за 13–110 с, и предел резал живые ответы. */
    /* ⚠️ WAIT_SONG_MS обязан быть НЕ МЕНЬШЕ серверного бюджета опроса
       (config ai-music.poll: first_delay_s + max_polls × interval_s = 765 с).
       Он был 720 000 — страница сдавалась на 45 с РАНЬШЕ сервера и говорила
       «дольше обычного» о песне, которую сервер в этот момент ещё дописывал и
       дописал (замер 09.09: 4 из 980 удачных генераций сели ровно в эту щель).
       Лестница повторов при временном отказе провайдера сдвигает удачные
       генерации ещё позже, поэтому щель нельзя было оставлять.
       Сторож — AiMusicWaitBudgetsAgreeTest. */
    var WAIT_PHRASE_MS = 180000, WAIT_VOICE_MS = 300000, WAIT_SONG_MS = 780000;

    /** Fallback for «slower than usual», until /voice/info says otherwise. */
    var SLOW_WAIT_MS = 40000;

    /* Поколение вызова фразы. Опрос, начатый для одного вызова, обязан умереть
     * вместе с ним: до 19.09.2026 опросы не отменялись вовсе и читали
     * `state.task` ВЖИВУЮ, поэтому осиротевший опрос (человек закрыл мастер и
     * начал заново) подхватывал СЛЕДУЮЩУЮ задачу и взводил фразу второй раз —
     * в обращении #7390 это видно как phrase_shown → phrase_shown через 1159 мс.
     * Второй взвод сбрасывает часы и тик посреди записи. */
    var phraseGen = 0;

    function pollPhrase(startedAt, gen) {
        var since = startedAt || Date.now();
        if (gen === undefined) gen = phraseGen;
        if (gen !== phraseGen) return;
        // Дольше обычного — сказать об этом, а не молчать «ждём слова».
        if (Date.now() - since > (state.phraseSlowMs || SLOW_WAIT_MS)) { sayWaitIsSlow(); }
        get(U.phrase + '?task=' + encodeURIComponent(state.task)).then(function (j) {
            if (gen !== phraseGen) return;   // этот вызов уже не наш
            // A damaged draw is redrawn server-side, and the redraw is a NEW task.
            // Following it here is what keeps the poll pointed at a task that will
            // actually hand over words.
            if (j.task && j.task !== state.task) { state.task = j.task; }
            if (j.phrase) { armPhrase(j.phrase, j.ttl); return; }
            /* ⚠️⚠️ ЭТО отказ по ОБРАЗЦУ, а не поломка.
             *
             * `processing_validate_fail` — вердикт Suno о присланном пении: в
             * записи не нашлось разборчивого голоса. Замер прод-логов 22.08
             * (user 5240, nginx: пять `voice/start` 200 → одиночный опрос
             * `voice/phrase` 123 байта → тишина) — пять таких вердиктов подряд,
             * и каждый раз страница говорила «Не получилось. Попробуйте ещё
             * раз — попытка бесплатна» И ЗАКРЫВАЛА МАСТЕР, унося запись.
             *
             * Тот же класс, что и `not_heard` неделей раньше: отказ называл не
             * тот орган. Человек шесть раз перепевал, потому что ему не сказали,
             * что перепевать надо ИНАЧЕ, а не БОЛЬШЕ.
             *
             * Мастер остаётся открытым, дубль — в плеере: чтобы понять, что не
             * так с записью, её надо СЛЫШАТЬ. */
            if (j.status === 'fail' || j.status === 'processing_validate_fail') {
                /* ⚠️⚠️ WHOSE fault, decided by Suno's own wording rather than by
                 * the status alone. Until 2026-08-30 every dead validation landed
                 * in the branch below and was announced as «Suno не нашёл в этой
                 * записи разборчивого голоса… запишите ближе к микрофону» — a
                 * sentence about the person's singing. Six such records pulled
                 * from `validate-info` that day (three of them from report #3269)
                 * ALL carried `errorMessage: "Internal Error, Please try again
                 * later."`, i.e. the provider falling over. Nobody's microphone
                 * was involved, and the advice sent people to re-record: five
                 * takes across 20 minutes in that one session. */
                if (j.fault === 'provider_fault') {
                    funnel('voice_failed', { reason: 'provider_fault' });
                    failWizard('provider_fault');
                    return;
                }
                funnel('voice_failed', { reason: 'sample_rejected' });
                say(T.err_sample_rejected || T.err_failed || '', 'error');
                warn(T.err_sample_rejected || '');
                showStep(1);
                return;
            }
            if (Date.now() - since > WAIT_PHRASE_MS) {
                /* The budget is the server's (phrase_wait_s), and the server has
                 * already redrawn a draw that went wordless for too long — so a
                 * phrase that has not arrived by now is a provider that is not
                 * answering, and the page says exactly that. (A dead provider does
                 * not produce late words: on 2026-08-30 three validations sat 163 s
                 * and died as `processing_validate_fail`. A slow one does — which is
                 * why the budget is no longer 90 s, see SunoVoiceService.) */
                funnel('voice_failed', { reason: 'phrase_timeout' });
                failWizard('phrase_timeout');
                return;
            }
            setTimeout(function () { pollPhrase(since, gen); }, 2500);
        }).catch(function () {
            if (gen !== phraseGen) return;
            if (Date.now() - since > WAIT_PHRASE_MS) { failWizard('phrase_timeout'); return; }
            setTimeout(function () { pollPhrase(since, gen); }, 4000);
        });
    }

    // ── step 2: say the phrase, against the clock ───────────────────────────
    var cdTimer = null;

    /**
     * Step 2 WITHOUT words — the state this wizard did not have, and the bug that
     * produced report #3269 («не могу записать голос, он не выдает слова, которые
     * нужно сказать», 2026-08-30).
     *
     * Step 2 was written as if a phrase always existed by the time it was shown.
     * It does not: `sendSample()` and `rephrase()` both open it while the words
     * are still being polled for, and when Suno's validate endpoint is down they
     * never arrive at all. What the person then saw, measured from that session's
     * own DOM telemetry — four `rage_click` rows on `#svoPhrase` with `txt:"…"` —
     * was a step headed «прочитайте фразу», a literal ellipsis where the words
     * belong, a countdown frozen at 1:40 that was never started, and a LIVE record
     * button. Every one of those says «your turn». None of them was true.
     *
     * So the invariant is now structural rather than remembered: the record button
     * and the clock are a function of `state.phrase`, and `armPhrase()` is the only
     * thing that can turn them on. Recording into a task with no phrase produced
     * Suno's 400 «Validate record is not in valid status» — 18 of those in nine
     * days — which the page then reported as «Попробуйте ещё раз, попытка
     * бесплатна»: an invitation to repeat the one move that cannot work.
     */
    /* Тик длительности записи живёт ТОЛЬКО во время записи. Раньше он показывал
       длину ПРОШЛОЙ записи и никем не сбрасывался: «0:05» висело рядом с
       неактивной кнопкой ещё до появления фразы и читалось как идущий отсчёт
       (скриншоты lallayeva 03.09, 17:29). Это было не часы, а несброшенное
       состояние — но человеку не отличить. */
    function resetPhraseTick() {
        var t = $('svoPhraseTime');
        if (!t) return;
        t.textContent = fmt(0);
        t.hidden = true;
    }

    function disarmPhrase() {
        phraseGen++;
        dropPhraseTake('disarm');
        forgetPhraseTake();
        state.phrase = '';
        state.phraseAt = 0;
        state.deadline = 0;
        stopCountdown();
        resetPhraseTick();
        $('svoPhrase').textContent = '…';
        var rec = $('svoPhraseRec');
        if (rec) { rec.disabled = true; }
        var clock = $('svoClockline');
        if (clock) { clock.hidden = true; }
        var wait = $('svoPhraseWait');
        if (wait) { wait.hidden = false; wait.textContent = T.phrase_wait || wait.textContent; }
        var exp = $('svoExpired');
        if (exp) { exp.hidden = true; }
        modeNote();
    }

    /** The waiting line, once the wait has outgrown «usually under a minute». */
    function sayWaitIsSlow() {
        var wait = $('svoPhraseWait');
        if (!wait || !T.phrase_wait_slow) return;
        wait.textContent = T.phrase_wait_slow.replace(':min', String(Math.ceil(WAIT_PHRASE_MS / 60000)));
    }

    function armPhrase(text, ttl) {
        phraseGen++;   // слова пришли — любой другой опрос этого мастера больше не нужен
        state.phrase = text;
        state.phraseAt = Date.now();
        // ⚠️ `ttl` is what the SERVER says is left of the phrase's life, and 0 is a
        // real answer. `ttl || 95` would turn «this phrase is already dead» into a
        // fresh minute and a half of countdown — the exact lie this clock exists to
        // avoid. Only a missing number falls back, and it falls back to the measured
        // life (SunoVoiceService::PHRASE_TTL_S), not to a rounder one.
        state.deadline = Date.now() + (typeof ttl === 'number' ? ttl : 95) * 1000;
        stage('phrase_shown');
        $('svoPhrase').textContent = text;
        resetPhraseTick();
        var rec = $('svoPhraseRec');
        if (rec) { rec.disabled = false; }
        var clock = $('svoClockline');
        if (clock) { clock.hidden = false; }
        var wait = $('svoPhraseWait');
        if (wait) { wait.hidden = true; }
        modeNote();
        showStep(2);
        startCountdown();
    }

    function startCountdown() {
        stopCountdown();
        cdTimer = setInterval(function () {
            var left = Math.max(0, Math.round((state.deadline - Date.now()) / 1000));
            $('svoClock').textContent = fmt(left);
            $('svoClock').classList.toggle('is-low', left <= 25);
            if (left <= 0) {
                stopCountdown();
                // Expired. Nothing was charged, and the sample is still good — so
                // offer the one action that helps instead of an error the user
                // cannot act on.
                /* Окно закрылось ПОСРЕДИ записи — запись кончается вместе с ним.
                   Раньше кнопка становилась disabled, а рекордер шёл дальше: остановить
                   его было нечем (и автоостановка на 30 с была click() по выключенной
                   кнопке — пустой звук). «Новая фраза» оживляла кнопку, ближайший тик
                   «нажимал» её, и к Suno уходил дубль на 60–180 с против НОВЫХ слов:
                   verify_too_long, 36 отказов / 9 сессий за 30 д, used_ms 130–475 мс. */
                var cut = dropPhraseTake('expired');
                forgetPhraseTake();
                $('svoExpired').hidden = false;
                $('svoPhraseRec').disabled = true;
                funnel('voice_window_expired', { mid_take: cut ? 1 : 0 });
            }
        }, 250);
    }

    function stopCountdown() {
        if (cdTimer) { clearInterval(cdTimer); cdTimer = null; }
    }

    /**
     * Ask for new words against the SAME singing.
     *
     * ⚠️⚠️ This used to send the person back to step 1 — «запишите пение ещё раз».
     * Suno's `regenerate` does refuse a task that already failed, and that fact got
     * read as «so the whole take has to be redone». It does not follow: a fresh
     * `validate` against the sample we already uploaded is the supported path, and
     * their copy of that upload lives three days.
     *
     * The cost of the old reading, measured on the first real user of this lane
     * (id 5625, 2026-08-22): ten refusals, each preceded by 37–154 s of singing
     * again, one hour, then a support ticket.
     */
    function rephrase(onFail) {
        if (state.busy) return;
        state.busy = true;
        showStep(2);
        disarmPhrase();
        funnel('voice_rephrase', {});

        post(U.rephrase, {}).then(function (j) {
            state.busy = false;
            if (!j.ok) {
                // No sample on the server any more (new session, or the wizard was
                // reset) — only then is starting over the honest answer.
                if (onFail) { onFail(); } else { showStep(1); say(T.again_hint || '', 'info'); }
                return;
            }
            state.task = j.task;
            // ⚠️ `disabled = false` used to live HERE, one line above the check for
            // whether words actually came back — so a rephrase against a silent
            // provider handed back a live record button and an ellipsis.
            if (j.phrase) { armPhrase(j.phrase, j.ttl); } else { pollPhrase(); }
        }).catch(function () {
            state.busy = false;
            if (onFail) { onFail(); } else { showStep(1); }
        });
    }

    on('svoAgain', 'click', function () { rephrase(); });

    var phraseRecording = false;
    /* Пик уровня за дубль — единственная защита от ЗАВЕДОМО мёртвой дорожки.
     *
     * ⚠️ Планка нарочно почти на нуле. Замер на живом API 22.08: дубль со средним
     * −44 дБ Suno ПРИНЯЛ. Значит «тихо» — не причина отказа, и резать по «тихо»
     * значит отвергать записи, которые прошли бы (ровно та ошибка, от которой
     * предупреждает шапка этого файла). Здесь ловится только плоская линия —
     * микрофон, не давший сигнала вообще.
     *
     * Ценность не в самой проверке, а в том, что фраза НЕ СГОРАЕТ: окно живёт
     * 100 секунд и одно на дубль, поэтому отправка немой дорожки стоила человеку
     * целого круга. */
    var PHRASE_FLATLINE_RMS = 0.004;
    var phrasePeak = 0;

    /* Дубль фразы ПРИНАДЛЕЖИТ фразе. Кончить его можно тремя способами, и все три —
     * функции, а не «нажатие кнопки» из кода: человек (finishPhraseTake), 30-секундный
     * потолок (он же, auto) и смерть самой фразы (dropPhraseTake — дубль
     * выбрасывается, потому что отвечать им уже не на что). */
    var lastTake = null;   // { take_s, auto } — едет в voice_phrase_sent

    function phraseTakeSeconds() {
        return recPhrase.started ? Math.round((Date.now() - recPhrase.started) / 100) / 10 : null;
    }

    function phraseButtonIdle() {
        var b = $('svoPhraseRec');
        if (b) { b.classList.remove('is-rec'); b.textContent = T.record_phrase || 'Record'; }
        resetPhraseTick();
    }

    function dropPhraseTake(why) {
        if (!phraseRecording) return false;
        phraseRecording = false;
        var sec = phraseTakeSeconds();
        phraseButtonIdle();
        recPhrase.stop();   // блоб намеренно никуда не идёт
        funnel('voice_take_dropped', { why: why, take_s: sec });
        return true;
    }

    function finishPhraseTake(auto) {
        if (!phraseRecording) return;
        phraseRecording = false;
        lastTake = { take_s: phraseTakeSeconds(), auto: auto ? 1 : 0 };
        phraseButtonIdle();
        var peak = phrasePeak;
        recPhrase.stop().then(function (blob) {
            if (!blob) return;
            if (peak < PHRASE_FLATLINE_RMS) {
                funnel('voice_take_silent', { peak: Math.round(peak * 10000) / 10000 });
                say(T.err_silent_take || T.err_mic || '', 'warning');
                return;   // фраза остаётся живой — человек просто пишет ещё раз
            }
            sendPhrase(blob);
        });
    }

    on('svoPhraseRec', 'click', function () {
        if (phraseRecording) { finishPhraseTake(false); return; }
        // Новый дубль — прошлый ответ репетиции больше не про то, что сейчас пишется.
        hideRehearsal();
        phrasePeak = 0;
        var tick = $('svoPhraseTime');
        if (tick) { tick.textContent = fmt(0); tick.hidden = false; }
        recPhrase.start(function (sec) {
            $('svoPhraseTime').textContent = fmt(sec);
            if (sec >= 30) finishPhraseTake(true);
        }, function (rms) {
            if (rms > phrasePeak) phrasePeak = rms;
            $('svoPhraseLed').style.setProperty('--lvl', Math.min(100, Math.round(rms * 300)) + '%');
        }).then(function () {
            phraseRecording = true;
            // Микрофон открывается не мгновенно (лист разрешения — секунды). Если за
            // это время фраза умерла, начинать запись не с чем: без этой проверки
            // рекордер стартовал бы ПОСЛЕ истечения, за уже выключенной кнопкой.
            if (!state.phrase || Date.now() >= state.deadline) { dropPhraseTake('late_start'); return; }
            $('svoPhraseRec').classList.add('is-rec');
            /* Нажатие этой кнопки ОТПРАВЛЯЕТ клип (см. ветку выше: stop → sendPhrase),
               поэтому подпись — «Готово — отправить», а не «Стоп»: «Стоп» пугает и
               обещает паузу, которой нет (замечание Фарида по скриншотам 03.09).
               У записи ПЕНИЯ остаётся «Стоп» — там остановка ведёт на прослушивание. */
            $('svoPhraseRec').textContent = T.phrase_done || T.stop || 'Done';
        }).catch(function () { say(T.err_mic || '', 'error'); });
    });

    /* Последняя запись фразы — чтобы «отправить как есть» после репетиции не
     * заставляло читать заново то, что человек уже прочитал.
     *
     * ⚠️⚠️ Она принадлежит СВОЕЙ фразе (поколение `phraseGen`), как и сам дубль.
     * До 31.10 она переживала и истечение окна, и «Новую фразу», а окно
     * репетиции с кнопкой «Всё равно отправить» оставалось на экране: чат 733 —
     * старый дубль ушёл через 208 с после показа своей фразы и один раз против
     * НОВОЙ фразы, которую человек ещё не произносил; за 10–31.10 так ушли
     * 6 из 17 «всё равно отправить». */
    var lastPhraseTake = null;   // { blob, gen }
    var voiceGen = 0;            // поколение опроса вердикта (pollVoice)

    function forgetPhraseTake() {
        lastPhraseTake = null;
        hideRehearsal();
        voiceGen++;
    }

    function phraseAlive() {
        return !!state.phrase && Date.now() < state.deadline;
    }

    function sendPhrase(blob, force) {
        if (blob) {
            lastPhraseTake = { blob: blob, gen: phraseGen };
        } else {
            // Повтор уже записанного — только для живой фразы, под которую он записан.
            if (!lastPhraseTake || lastPhraseTake.gen !== phraseGen || !phraseAlive()) { forgetPhraseTake(); return; }
            blob = lastPhraseTake.blob;
        }
        if (!blob) return;
        voiceGen++;   // прежний опрос вердикта, если жив, больше не наш
        stopCountdown();
        showStep(3);
        // Сколько секунд человеку понадобилось от показа фразы до отправки.
        // Именно это число говорит, тесно ли окно, — а не наши предположения.
        var usedMs = state.phraseAt ? (Date.now() - state.phraseAt) : null;
        stage('phrase_recorded');
        // take_s / auto: сколько длился САМ дубль и кончил ли его 30-секундный потолок.
        // `used_ms` — от показа фразы, в нём раздумья; у #7390 все три дубля шли ~30 с на
        // фразу в восемь слов, и отличить «не знал, что запись завершают кнопкой» от
        // «пел вместо чтения» было нечем. `force` — повтор уже записанного дубля.
        funnel('voice_phrase_sent', { used_ms: usedMs, left_ms: Math.max(0, state.deadline - Date.now()),
            take_s: lastTake ? lastTake.take_s : null, auto: lastTake ? lastTake.auto : null, force: force ? 1 : 0 });

        var fd = new FormData();
        fd.append('verify', blob, 'verify.webm');
        fd.append('task', state.task);
        fd.append('phrase', state.phrase);
        fd.append('consent', '1');
        fd.append('name', ($('svoName').value || '').trim());
        fd.append('skill', $('svoSkill').value || 'beginner');

        if (force) fd.append('force', '1');

        var gen = phraseGen;
        post(U.confirm, fd).then(function (j) {
            if (gen !== phraseGen) return;   // фраза сменилась, пока шёл запрос
            /* Сервер не отправляет к Suno дубль фразы, которой уже нет
               (PHRASE_DEAD_S): это не отказ голоса, а «время вышло». */
            if (!j.ok && j.error === 'phrase_expired') { showPhraseDead(); return; }
            if (!j.ok) { failWizard(j.error); return; }
            /* РЕПЕТИЦИЯ отказала — до Suno запись не ушла, фраза ЖИВА, и слушать
               её заново не нужно. Говорим, что услышали (или что не услышали
               ничего — это микрофон, а не дикция). «Отправить как есть» — только
               там, где наше ухо может ошибиться (тишина, обрывок фразы); где оно
               услышало целый ДРУГОЙ текст (`other_text`), Suno не принял ни
               одного такого дубля из 17 — кнопки нет. */
            if (j.status === 'rehearsal') {
                funnel('phrase_rehearsal_stop', { silent: j.reason === 'silent' ? 1 : 0,
                    other: j.reason === 'other_text' ? 1 : 0, force: force ? 1 : 0 });
                showRehearsal(j.reason, j.heard || '');
                return;
            }
            pollVoice(state.task, voiceGen);
        }).catch(function () { if (gen === phraseGen) failWizard('network'); });
    }

    function hideRehearsal() {
        var box = $('svoRehearsal');
        if (box) box.hidden = true;
    }

    /** Время фразы вышло, пока дубль шёл на сервер: шаг 2, «Новая фраза». */
    function showPhraseDead() {
        stopCountdown();
        forgetPhraseTake();
        showStep(2);
        state.deadline = 0;
        $('svoExpired').hidden = false;
        $('svoPhraseRec').disabled = true;
        funnel('voice_window_expired', { mid_take: 0, server: 1 });
    }

    function showRehearsal(reason, heard) {
        showStep(2);
        var box = $('svoRehearsal');
        if (!box) { say(T.rh_mismatch || T.err_not_heard || '', 'warning'); return; }
        var line = reason === 'silent'
            ? (T.rh_silent || '')
            : ((reason === 'other_text' ? (T.rh_other || T.rh_mismatch) : T.rh_mismatch) || '').replace(':heard', heard);
        var msg = $('svoRehearsalMsg');
        if (msg) msg.textContent = line;
        var force = $('svoRehearsalForce');
        if (force) force.hidden = reason === 'other_text';
        box.hidden = false;
        startCountdown();   // фраза жива — верните человеку её часы
    }

    on('svoRehearsalRetry', 'click', hideRehearsal);
    on('svoRehearsalForce', 'click', function () {
        hideRehearsal();
        sendPhrase(null, true);
    });

    /* Опрос вердикта принадлежит СВОЕЙ отправке: задача — та, что ушла, а не
     * `state.task` на момент очередного опроса. До 31.10 опрос читал задачу
     * заново каждые 3 с и после «Новой фразы» переходил на новую — в чате 733
     * два опроса шли по одной задаче и дали два отказа подряд за 3 с.
     * Счётчик `voiceGen` объявлен рядом с `lastPhraseTake`. */

    function pollVoice(task, gen, startedAt) {
        if (gen !== voiceGen) return;
        var since = startedAt || Date.now();
        get(U.state + '?task=' + encodeURIComponent(task)).then(function (j) {
            if (gen !== voiceGen) return;
            if (j.status === 'success' && j.voice) {
                stage('voice_ready');
                // `after` / `read`: прошёл ли голос после отказа «не узнан» и каким
                // способом — единственный способ узнать, помог ли совет петь фразу.
                // `cov` — сколько слов фразы услышало наше ухо в принятом дубле.
                funnel('voice_created', { used_ms: state.phraseAt ? (Date.now() - state.phraseAt) : null,
                    after: state.voiceRefusals, read: state.readMode ? 1 : 0,
                    cov: typeof j.cov === 'number' ? j.cov : null });
                state.voiceRefusals = 0;
                state.readMode = false;
                state.voices = j.voices || state.voices;
                state.picked = j.voice.id;
                renderVoices();
                closeWizard();
                say(T.voice_ready || '', 'success');
                return;
            }
            // `wait_ms` — сколько Suno думал над вердиктом. Замер 24.09: «Internal
            // Error» за 15–20 с приходил на несовпадение голоса, но однажды — после
            // 150+ с зависания на совпадающих записях. Отличать ли их — решат числа.
            if (j.status === 'fail') { failWizard(j.reason || 'failed', j.said, Date.now() - since, j); return; }
            if (Date.now() - since > WAIT_VOICE_MS) { failWizard('voice_timeout'); return; }
            setTimeout(function () { pollVoice(task, gen, since); }, 3000);
        }).catch(function () {
            if (gen !== voiceGen) return;
            if (Date.now() - since > WAIT_VOICE_MS) { failWizard('voice_timeout'); return; }
            setTimeout(function () { pollVoice(task, gen, since); }, 4000);
        });
    }

    function failWizard(code, said, waitMs, ear) {
        if (code === 'mismatch') state.voiceRefusals++;
        // Причина отказа — то, чем «промахнулся» отличается от «не успел». `said` —
        // какой именно фразой ответил Suno (SunoVoiceService::providerSaid), `n` —
        // который это отказ «голос не узнан» подряд, `read` — в режиме чтения ли.
        // `cov` — сколько слов фразы услышало наше ухо; `heard` — сказали ли
        // человеку, что именно услышано (вердикт «слова» из двух фактов, сервер).
        var heard = ear && typeof ear.heard === 'string' ? ear.heard : '';
        funnel('voice_failed', { reason: code, said: said || null, n: state.voiceRefusals,
            read: state.readMode ? 1 : 0, wait_ms: typeof waitMs === 'number' ? Math.round(waitMs) : null,
            used_ms: state.phraseAt ? (Date.now() - state.phraseAt) : null,
            cov: ear && typeof ear.cov === 'number' ? ear.cov : null, heard: heard ? 1 : 0 });
        var text = reasonText(code);
        /* Отказ о СЛОВАХ называет, что услышано: «Мы услышали: «Утро стучит в
         * окно…»» объясняет человеку, что он записал не фразу, точнее любого
         * совета (чат 733: девять дублей песни вместо фразы). */
        if (code === 'not_heard' && heard && T.heard_line) {
            text = T.heard_line.replace(':heard', heard) + ' ' + text;
        }
        if (code === 'mismatch') {
            /* Первый отказ — совет петь фразу (он в `err_mismatch`). Второй подряд
             * — уже не совет, а другой путь: обе записи одним способом, чтением.
             * В режиме чтения повторять «пропойте» бессмысленно — там своя фраза. */
            if (state.readMode) text = T.err_mismatch_read || text;
            else if (state.voiceRefusals >= 2) text = T.err_mismatch_again || text;
        }
        $('svoFailText').textContent = text;
        var reread = $('svoReread');
        if (reread) reread.hidden = !(code === 'mismatch' && state.voiceRefusals >= 2 && !state.readMode);
        $('svoFail').hidden = false;
        showStep(3);
        $('svoWorking').hidden = true;
    }

    /* Второй путь после двух «голос не узнан» подряд: образец заново, но
     * ЧТЕНИЕМ — и фраза тоже чтением. Одна и та же манера в обеих записях: на
     * живом Suno 24.09 — 8 из 9, и единственный неуспех был зависанием самого
     * сервиса, а не отказом по голосу. */
    on('svoReread', 'click', function () {
        funnel('voice_reread', { n: state.voiceRefusals });
        state.readMode = true;
        $('svoFail').hidden = true;
        $('svoWorking').hidden = false;
        openWizard();
    });

    on('svoRetry', 'click', function () {
        $('svoFail').hidden = true;
        $('svoWorking').hidden = false;
        // Every refusal this button answers — wrong words, dead phrase, a voice
        // Suno would not confirm — is about the WORDS. The singing is still good
        // and still uploaded, so ask for new words; fall back to step 1 only if the
        // server no longer has the sample.
        rephrase(function () { openWizard(); });
    });

    // Форма кавера раскрывается ТОЛЬКО после выбора — до этого человек ещё не
    // решил, какую работу делает, и семь полей ему ничего не сообщают.
    // ⚠️ Было булевым (`w = which === 'write'`), то есть ровно на два состояния.
    // Вкладок стало три: у третьей другой ДВИЖОК (наш SVC + разделение стемов),
    // и она открывает не форму Suno, а соседний блок страницы. Булево «write или
    // не write» третью вкладку молча превратило бы во вторую.
    var TABS = [['svoTabWrite', 'write'], ['svoTabCover', 'cover'], ['svoTabSong', 'song']];
    /* ── Классический лейн — модуль по требованию ─────────────────────────
     *
     * Вкладка «кавер на свою песню» (режим D) — это старая SVC-мастерская:
     * 105 КБ скрипта, 26 КБ стилей и шесть библиотек. До 09.09.2026 всё это
     * лежало инлайном в странице и ехало КАЖДОМУ, кто открыл её ради «Мой
     * голос», а на плохой сети страница в 841 КБ шла 48–78 с (заявка S.V.).
     * Теперь список файлов даёт сервер (data-classic-assets, версии в
     * адресах), а грузятся они при первом открытии вкладки, по порядку:
     * библиотеки, потом модуль, который объявляет `window.__vcoSongMode`. */
    var classicLoad = null;
    function loadOne(tag, attrs) {
        return new Promise(function (res, rej) {
            var el = document.createElement(tag);
            Object.keys(attrs).forEach(function (k) { el[k] = attrs[k]; });
            el.onload = function () { res(true); };
            el.onerror = function () { rej(new Error(tag + ' failed: ' + (attrs.src || attrs.href))); };
            document.head.appendChild(el);
        });
    }
    function ensureClassic() {
        if (typeof window.__vcoSongMode === 'function') return Promise.resolve(true);
        if (classicLoad) return classicLoad;
        var assets = {};
        try { assets = JSON.parse(root.dataset.classicAssets || '{}') || {}; } catch (e) { assets = {}; }
        var css = assets.css || [], js = assets.js || [];
        if (!js.length) return Promise.resolve(false);
        // Стиль — параллельно и без права уронить загрузку: голый, но рабочий
        // блок лучше кнопки, которая ничего не делает.
        var cssDone = Promise.all(css.map(function (href) {
            return loadOne('link', { rel: 'stylesheet', href: href }).catch(function () { return false; });
        }));
        // Библиотеку, которую страница уже принесла (mic-silence-watch грузит
        // и лейн Suno), второй раз не просим: тот же путь без ?v= — тот же файл.
        function present(src) {
            var path = src.replace(/\?.*$/, '');
            return [].some.call(document.scripts, function (s) {
                return s.src && s.src.replace(/\?.*$/, '').slice(-path.length) === path;
            });
        }
        classicLoad = js.reduce(function (p, src) {
            return p.then(function () { return present(src) ? true : loadOne('script', { src: src, async: false }); });
        }, Promise.resolve()).then(function () {
            return cssDone;
        }).then(function () {
            return typeof window.__vcoSongMode === 'function';
        }).catch(function (e) {
            classicLoad = null;
            try {
                window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'svo_classic_load_failed', why: String((e && e.message) || e).slice(0, 80) });
            } catch (_) {}
            return false;
        });
        return classicLoad;
    }

    function showTab(which) {
        var t = (which === 'cover' || which === 'song') ? which : 'write';
        state.tab = t;
        $('svoWrite').hidden = t !== 'write';
        if ($('svoCoverForm')) $('svoCoverForm').hidden = t !== 'cover';
        // Наш лейн — отдельный блок ниже по странице, а не форма внутри этого.
        var ours = document.getElementById('vcoWrap');
        if (ours) {
            if (t === 'song') {
                ensureClassic().then(function (loaded) {
                    // Пока грузился, человек мог уйти на другую вкладку.
                    if (state.tab !== 'song') return;
                    if (!loaded) { say(T.err_failed || '', 'error'); return; }
                    ours.hidden = false;
                    window.__vcoSongMode(laneFile);
                    laneFile = null;
                });
            } else {
                ours.hidden = true;
            }
        }
        // Всё, что говорит про ЭТОТ лейн (его цена, его замок), при открытой чужой
        // вкладке прячется: цена Suno над нашей формой — не украшение, а неверное
        // число прямо над кнопкой, которая спишет другое.
        document.querySelectorAll('[data-svo-suno-only]').forEach(function (el) {
            el.hidden = t === 'song';
        });
        TABS.forEach(function (p) {
            var b = $(p[0]);
            if (!b) return;
            b.classList.toggle('is-on', t === p[1]);
            b.setAttribute('aria-selected', t === p[1] ? 'true' : 'false');
        });
    }
    /* Открытие вкладки до 19.09.2026 не мерилось вовсе: третья («музыка останется,
       голос станет вашим») голоса Suno не требует, а нашёл ли её хоть кто-то из
       застрявших на шаге 1 — ответить было нечем. `voices` — был ли у человека голос. */
    function tabOpened(which) {
        funnel('tab_open', { which: which, voices: (state.voices || []).length });
        showTab(which);
    }
    on('svoTabWrite', 'click', function () { tabOpened('write'); });
    on('svoTabCover', 'click', function () { tabOpened('cover'); });
    on('svoTabSong', 'click', function () { tabOpened('song'); });

    // ── ударения ────────────────────────────────────────────────────────────
    // Общий модуль RussianAccent, а не своя реализация: словарь на 535 тысяч
    // словоформ живёт в одном месте, и вторая копия разошлась бы молча.
    var accentBtn = $('svoAccent');
    if (accentBtn) {
        accentBtn.addEventListener('click', function () {
            var ta = $('svoLyrics');
            if (!ta.value.trim()) return;
            accentBtn.disabled = true;
            var run = function () {
                try {
                    ta.value = window.RussianAccent.accentuate(ta.value, { skipMarked: true });
                    $('svoAccentNote').textContent = T.accent_done || '';
                } catch (e) { say(T.err_failed || '', 'error'); }
                accentBtn.disabled = false;
            };
            if (window.RussianAccent && window.RussianAccent.isReady()) { run(); return; }
            if (!window.RussianAccent) { accentBtn.disabled = false; say(T.err_failed || '', 'error'); return; }
            window.RussianAccent.load({}).then(run).catch(function () {
                accentBtn.disabled = false; say(T.err_failed || '', 'error');
            });
        });
    }

    // Выбранный файл вкладки «Перепеть готовую». Живёт рядом с замками, потому
    // что syncRunLocks() читает его раньше, чем выполнится обработчик выбора.
    var coverFile = null;
    // Файл, который поедет в наш лейн вместе с человеком, если Suno откажет по
    // каталогу: просить выбрать его заново — тот же отказ, только вежливый.
    var laneFile = null;

    /**
     * Идёт ли СЕЙЧАС оплаченная работа. Пока идёт — второй запуск невозможен ни
     * с одной вкладки: обе тратят один кошелёк, и второй резерв не ускоряет
     * первый, а только отнимает у него токены.
     */
    function jobRunning() { return !!state.activeJobId; }

    /** Причина, по которой кнопки заперты, — рядом с кнопками, а не в тосте. */
    function syncRunLocks() {
        var note = $('svoRunLock');
        if (note) note.hidden = !jobRunning();
        syncSing();
        syncCover();
    }

    /* Та же схема у «Перепеть готовую»: кнопка открыта, когда файл выбран и стиль
     * с текстом написаны, а строка под ней говорит, чего не хватает. До 28.10 кнопка
     * оживала от одного файла, а про стиль и текст отвечал тост после нажатия, без
     * следа в телеметрии (Dgms, u=8651, 24.10: выбрал песню, потрогал поле стиля и
     * ушёл — было ли нажатие, неизвестно). Сервер проверяет всё сам. */
    function coverMissing() {
        if (!coverFile) return 'file';
        if ((($('svoCoverStyle') && $('svoCoverStyle').value) || '').trim().length < 2) return 'style';
        if ((($('svoCoverLyrics') && $('svoCoverLyrics').value) || '').trim().length < 2) return 'lyrics';
        return '';
    }
    function syncCover() {
        var cg = $('svoCoverGo');
        if (!cg) return;
        var missing = coverMissing();
        cg.disabled = !!missing || state.busy || jobRunning();
        if ($('svoCoverGate')) $('svoCoverGate').hidden = !missing;
    }
    ['svoCoverStyle', 'svoCoverLyrics'].forEach(function (id) {
        on(id, 'input', syncCover);
    });

    /**
     * Кнопка генерации открыта ровно тогда, когда шаги действительно пройдены:
     * голос готов и выбран, текст и стиль не пусты. Раньше она была живой всегда
     * и отвечала отказом ПОСЛЕ нажатия — на платном туле это читается как
     * поломка, а не как «вы ещё не всё заполнили».
     * ⚠️ Это удобство, а НЕ проверка: сервер по-прежнему проверяет всё сам —
     * запертая кнопка обходится одной строкой в консоли.
     */
    function syncSing() {
        var btn = $('svoSing');
        if (!btn) return;
        var ready = !!state.picked
            && (($('svoLyrics') && $('svoLyrics').value) || '').trim().length >= 2
            && (($('svoStyle')  && $('svoStyle').value)  || '').trim().length >= 2;
        btn.disabled = !ready || state.busy || jobRunning();
        if ($('svoGate')) $('svoGate').hidden = ready;
    }
    ['svoLyrics', 'svoStyle'].forEach(function (id) {
        on(id, 'input', syncSing);
    });

    // Регуляторы уходят только тронутыми. Отправлять их положение всегда — то
    // же самое, что заменить умолчания провайдера своими у всех разом.
    var weightsTouched = false;
    [['svoWStyle', 'svoWStyleOut'], ['svoWWeird', 'svoWWeirdOut']].forEach(function (pair) {
        var el = $(pair[0]), out = $(pair[1]);
        if (!el) return;
        var show = function () { if (out) out.textContent = el.value; };
        show();
        el.addEventListener('input', function () { weightsTouched = true; show(); });
    });

    // ── «Спеть моим голосом»: генерация ЗДЕСЬ, тем же конвейером ────────────
    on('svoSing', 'click', function () { submitSong(false); });
    function submitSong(shortOk) {
        if (state.busy || jobRunning()) return;
        var lyrics = ($('svoLyrics').value || '').trim();
        var style  = ($('svoStyle').value || '').trim();
        if (lyrics.length < 2) { say(T.err_lyrics || '', 'warning'); return; }
        if (style.length < 2)  { say(T.err_style || '', 'warning'); return; }

        state.busy = true;
        $('svoSing').disabled = true;
        $('svoSingBusy').hidden = false;
        hideErr();
        stage('song_submit');

        // Тело запроса живёт в переменной, потому что «Создать как есть»
        // повторяет ЕГО ЖЕ с `short_ok` — не новую сборку из полей, которые
        // человек мог успеть поправить.
        var payload = {
            tool: 'song',
            lyrics: lyrics,
            style_custom: style,
            title: ($('svoTitle').value || '').trim(),
            duration_hint: $('svoDur').value,
            exclude: ($('svoExclude') ? ($('svoExclude').value || '').trim() : ''),
            gen_weighted: weightsTouched ? 1 : 0,
            style_weight: $('svoWStyle') ? $('svoWStyle').value : '',
            weirdness: $('svoWWeird') ? $('svoWWeird').value : '',
            persona_id: state.picked,
            vocal_lang: (window.__LOCALE || 'ru'),
            solo_voice: checked('svoSolo') ? 1 : 0,
            short_ok: shortOk ? 1 : 0
        };
        post(U.generate, payload).then(function (j) {
            state.busy = false;
            syncSing();
            $('svoSingBusy').hidden = true;
            if (!j.ok) {
                if (j.error === 'lyrics_too_short') {
                    // Вопрос, а не отказ: сервер ничего не списал и ждёт
                    // подтверждения. Повтор — тем же телом плюс `short_ok`.
                    funnel('song_refused', { kind: 'lyrics_too_short', est: j.estimate_sec || 0, lines: j.sung_lines || 0 });
                    showErr(j.msg || failMsg(j), { ask: true, again: function () { submitSong(true); } });
                    return;
                }
                if (j.error === 'insufficient_tokens') {
                    var need = typeof j.need === 'number' ? j.need : state.cost;
                    var bal  = typeof j.balance === 'number' ? j.balance : (state.balance || 0);
                    funnel('song_refused', { kind: 'insufficient', short: Math.max(0, need - bal) });
                    showErr(failMsg(j), { topup: true, short: Math.max(0, need - bal) });
                    return;
                }
                funnel('song_refused', { kind: String(j.error || 'other').slice(0, 40) });
                say(failMsg(j), 'error');
                // Отказ по мёртвому голосу — единственный, после которого список
                // выше УЖЕ неверен: перечитываем его, чтобы голос стал
                // зачёркнутым сразу, а не при следующем заходе на страницу.
                if (j.error === 'voice_expired') loadInfo();
                return;
            }
            funnel('song_started', {});
            watchGeneration(j.id);
        }).catch(function () {
            state.busy = false; $('svoSingBusy').hidden = true; syncSing();
            // Сюда попадает только оборванный запрос (ответ сервера, даже отказ,
            // приходит в `.then`) — та же фраза, что у отправки образца голоса.
            say(T.err_network || T.err_failed || '', 'error');
        });
    }

    // ── «перепеть готовую песню моим голосом» ───────────────────────────────
    // Rides the SAME generation pipeline as the song tool (upload-source →
    // track-action → status), so there is one wallet, one history and one place
    // where a Suno change has to be absorbed. Only the form is local.
    /* Файл для «Перепеть готовую» читает СТРАНИЦА, и сразу при выборе.
     *
     * Между «выбрал» и «отправил» здесь по устройству формы минуты: стиль и
     * слова обязательны, за словами уходят в другое приложение. А `File` из
     * пикера — не байты, а путь с отметкой времени, и на Android читает его
     * потом сетевой процесс браузера, у которого это не получается там, где у
     * страницы получается (разбор — __uploadBody в timbrica-core.js). Заявка на
     * возврат №41, 06.10.2026: три нажатия подряд, три «Не получилось.
     * Попробуйте ещё раз — попытка бесплатна», ни одного запроса на сервере —
     * повтор не мог помочь никогда. Ещё два аккаунта с Премиумом за ту же
     * неделю: 12 таких отказов.
     *
     * Поэтому копию делаем в момент выбора (FileBytes.prime — та же лестница
     * чтения, что у общей зоны загрузки), а если файл не читается и у нас —
     * говорим это ТУТ ЖЕ и тем же предложением, что зона (__unreadableFileMsg:
     * облачный файл надо сначала скачать на устройство), а не после того, как
     * человек набрал текст песни. */
    function dropCover() {
        coverFile = null;
        var inp = $('svoCoverFile');
        if (inp) { try { inp.value = ''; } catch (e) {} }
        if ($('svoCoverName')) $('svoCoverName').textContent = '';
        syncRunLocks();
    }
    function fileUnreadableText(err) {
        return (window.__uploadBody && window.__uploadBody.text(err))
            || (window.__unreadableFileMsg && window.__unreadableFileMsg('read_error'))
            || T.err_failed || '';
    }
    function holdCover(file) {
        var FB = window.FileBytes;
        if (!file || !FB || typeof FB.prime !== 'function') return;
        FB.prime(file).catch(function (err) {
            if (coverFile !== file) return;                    // уже выбран другой
            var kind = (err && err.fileLostKind) || 'read_error';
            if (kind === 'oom' || kind === 'aborted') return;  // не про файл
            dropCover();
            showErr(fileUnreadableText(err), {});
            funnel('cover_upload_failed', { at: 'pick', kind: 'file', why: kind });
        });
    }
    on('svoCoverFile', 'change', function (e) {
        coverFile = e.target.files && e.target.files[0];
        $('svoCoverName').textContent = coverFile ? coverFile.name : '';
        syncRunLocks();
        holdCover(coverFile);
    });

    on('svoCoverGo', 'click', function () {
        if (!coverFile || state.busy || jobRunning()) return;
        var style = ($('svoCoverStyle').value || '').trim();
        var lyr   = ($('svoCoverLyrics').value || '').trim();
        var missing = coverMissing();
        if (missing) {
            funnel('cover_form_refused', { why: missing });
            say((missing === 'style' ? T.err_style : T.err_lyrics) || '', 'warning');
            return;
        }

        state.busy = true;
        $('svoCoverGo').disabled = true;
        $('svoCoverBusy').hidden = false;
        hideErr();

        var fd = new FormData();
        fd.append('audio', coverFile, coverFile.name);
        /* Где оборвалось — знает шаг, а не текст ошибки: у чтения файла, у
         * отправки и у ответа сервера три разных ответа человеку. */
        var step = 'read', t0 = Date.now();
        /* Файл уходит ВПЕРЁД порциями по измеренной скорости (slices — разбор
         * в __uploadBody): одна длинная отправка песни — это то, что рвёт
         * телефонный аплинк. Первая порция = чтение кончилось, дальше — сеть.
         * Сервер забыл присланный файл (410 upload_gone) — один повтор. */
        var send = function (fresh) {
            return (window.__uploadBody
                ? window.__uploadBody(fd, { slices: true, fresh: fresh, onProgress: function () { step = 'upload'; } })
                : Promise.resolve(fd)).then(function (body) {
                step = 'upload';
                return post(U.upload, body);
            }).then(function (up) {
                return (!fresh && up && up.__status === 410 && up.error_kind === 'upload_gone') ? send(true) : up;
            });
        };
        send(false).then(function (up) {
            if (!up.ok) {
                // Отказ сервера несёт свои слова («файл больше 45 МБ», «только
                // MP3 и WAV») — раньше они выбрасывались вместе с ответом.
                var refused = new Error('upload_refused');
                refused.refusal = up;
                throw refused;
            }
            step = 'action';
            /* ⚠️⚠️⚠️ Имена полей — КОНТРАКТ с `AiMusicController::trackAction()`,
             * и до 23.08 они не совпадали ни одним из трёх мест:
             *
             *   `action:'cover'`  → сервер читает `operation` → 422
             *                       «Неизвестная операция.» ВСЕГДА;
             *   `cover_params:{…}`→ сервер читает `cover_weighted` + плоские
             *                       `style_weight`/`weirdness`/`audio_weight`;
             *   `vocal_gender`    → на загруженном кавере вообще не читался
             *                       (наследовался только у родительского трека).
             *
             * Первое означает, что вкладка «Перепеть готовую» не работала НИ
             * РАЗУ с самого запуска (`9e88a26f86`, 21.08). Прод-лог 22.08: пять
             * пар `upload-source 200` → `track-action 422` за два с половиной
             * часа у одного человека, файл 33 МБ каждый раз.
             *
             * ⭐ Почему это пережило проверку: лейн был прогнан по API, а не по
             * СТРАНИЦЕ — тот же вывод, что и неделей раньше. Сквозной curl видит
             * контракт сервера с самим собой; имена полей, которые шлёт форма,
             * он не видит никогда. Сторож теперь связывает их напрямую —
             * `SunoVoiceCoverContractTest`. */
            /* Ползунки здесь НЕ опциональные, в отличие от песенного тула: их
             * умолчания (audioWeight 0.9) и есть смысл этой вкладки — держаться
             * исходной записи. Поэтому `cover_weighted` уходит всегда, а не по
             * факту «человек их трогал». */
            return post(U.action, {
                operation: 'cover',
                source_path: up.path,
                style_custom: style,
                lyrics: lyr,
                title: val('svoCoverTitle', '').trim(),
                persona_id: state.picked,
                // Every knob Suno accepts on this endpoint, so the page is not
                // quietly poorer than the API behind it.
                vocal_gender: val('svoGender', 'auto'),
                // Единственный рычаг языка: фонетического слоя у Suno нет.
                vocal_lang: (window.__LOCALE || 'ru'),
                solo_voice: checked('svoCoverSolo') ? 1 : 0,
                cover_weighted: 1,
                style_weight: numOr('svoStyleW', 0.75),
                weirdness:    numOr('svoWeird',  0.3),
                audio_weight: numOr('svoAudioW', 0.9)
            });
        }).then(function (j) {
            step = 'answer';
            state.busy = false;
            $('svoCoverBusy').hidden = true;
            $('svoCoverGo').disabled = false;
            if (!j.ok) {
                if (j.error === 'insufficient_tokens') {
                    var need = typeof j.need === 'number' ? j.need : state.cost;
                    var bal  = typeof j.balance === 'number' ? j.balance : (state.balance || 0);
                    funnel('song_refused', { kind: 'insufficient', short: Math.max(0, need - bal), via: 'cover' });
                    showErr(failMsg(j), { topup: true, short: Math.max(0, need - bal) });
                    return;
                }
                funnel('song_refused', { kind: String(j.error || 'other').slice(0, 40), via: 'cover' });
                say(failMsg(j), 'error');
                // Как у «Написать песню»: после отказа по мёртвому голосу список уже неверен.
                if (j.error === 'voice_expired') loadInfo();
                return;
            }
            funnel('song_started', { via: 'cover' });
            watchGeneration(j.id || j.generation_id);
        }).catch(function (err) {
            state.busy = false;
            $('svoCoverBusy').hidden = true;
            $('svoCoverGo').disabled = false;
            var ms = Date.now() - t0;
            /* Три разных отказа — три разных ответа. Раньше все три (и любая
             * наша собственная ошибка после ответа) были одной фразой «Не
             * получилось. Попробуйте ещё раз — попытка бесплатна», и для двух из
             * них это приглашение жать кнопку, которая сработать не может. */
            if (err && err.refusal) {
                funnel('cover_upload_failed', { at: 'upload', kind: 'refused', code: String(err.refusal.error || ''), http: err.refusal.__status || 0, ms: ms });
                showErr(failMsg(err.refusal), {});
                return;
            }
            if (window.__uploadBody && window.__uploadBody.kind(err) === 'file') {
                // Файл не читается у нас самих: повтор не поможет, нужен другой путь к файлу.
                funnel('cover_upload_failed', { at: step, kind: 'file', why: (err && err.fileLostKind) || '', ms: ms });
                dropCover();
                showErr(fileUnreadableText(err), {});
                return;
            }
            if (step === 'upload' || step === 'action') {
                // Копия файла у нас была, запрос не дошёл или ответ не пришёл — это сеть.
                funnel('cover_upload_failed', { at: step, kind: 'network', ms: ms, err: String((err && err.name) || '').slice(0, 40) });
                say(T.err_network || T.err_failed || '', 'error');
                return;
            }
            // Чтение упёрлось не в файл (память), либо ответ пришёл и упали мы
            // сами — тут нечего «пробовать ещё раз»: общий разбор ошибок назовёт
            // память, а нашу ошибку унесёт в телеметрию со стеком.
            funnel('cover_upload_failed', { at: step, kind: 'other', ms: ms, err: String((err && err.name) || '').slice(0, 40) });
            if (window.__handleError) window.__handleError(err, 'cover_submit');
        });
    });

    /* ── «Сочинить текст с ИИ» ────────────────────────────────────────────
     *
     * Тот же маршрут /api/ai-music/lyrics, тот же кошелёк и те же подписи, что
     * у песенного тула. Причина, по которой это нужно ИМЕННО здесь: человек
     * приходит сюда петь СВОИМ голосом, и слов у него чаще всего нет. Уходить
     * за ними на соседнюю страницу — терять записанный голос и половину
     * пришедших.
     *
     * Ошибки разбираются по КОДУ ответа, а не по тексту: 402 — не хватает
     * токенов (это не поломка, это касса), 422 — либо тема, либо модерация,
     * 429 — частота, 503 — лейн выключен. Один общий «не получилось» на все
     * четыре случая отправил бы человека чинить не то. */
    (function lyricsComposer() {
        var openBtn = $('svoLgOpen'), panel = $('svoLgPanel');
        if (!openBtn || !panel || !U.lyricsGen) return;

        var theme = $('svoLgTheme'), moods = $('svoLgMoods'), len = $('svoLgLength'),
            go = $('svoLgGo'), status = $('svoLgStatus'), bar = $('svoLgBar');
        // Целиком или никак: половина панели хуже её отсутствия, а ровно
        // такая половина и уносила инициализацию модуля до 22.08.
        if (!theme || !moods || !len || !go || !status || !bar) return;
        var mood = '', busy = false;

        function tell(msg, isErr) {
            status.textContent = msg || '';
            status.classList.toggle('is-err', !!isErr);
        }

        on(openBtn, 'click', function () {
            var open = panel.hidden;
            panel.hidden = !open;
            openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (open) { try { theme.focus({ preventScroll: true }); } catch (e) {} }
        });

        on(moods, 'click', function (e) {
            var b = e.target.closest('.svo-lg-mood');
            if (!b) return;
            var picked = b.getAttribute('data-mood') || '';
            // Повторный щелчок снимает настроение — иначе выбранное однажды
            // никак не отменить, а «без настроения» это законный выбор.
            var same = (mood === picked);
            mood = same ? '' : picked;
            [].forEach.call(moods.children, function (x) { x.classList.remove('is-on'); });
            if (!same) b.classList.add('is-on');
        });

        function setBusy(on) {
            busy = on;
            go.disabled = on;
            bar.hidden = !on;
            if (on) tell(T.lg_going || '');
        }

        on(go, 'click', function () {
            if (busy) return;
            var th = (theme.value || '').trim();
            if (th.length < 3) {
                tell(T.lg_theme_needed || '', true);
                try { theme.focus({ preventScroll: true }); } catch (e) {}
                return;
            }
            setBusy(true);
            funnel('lyrics_generate', { mood: mood ? 1 : 0, length: len.value || 'normal' });

            // Тот же язык, что этот тул уже шлёт в генерацию (строки 1509 и 1607):
            // сочинитель текста обязан писать на том, чем человек собирается петь.
            post(U.lyricsGen, { theme: th, mood: mood, length: len.value || 'normal', vocal_lang: (window.__LOCALE || 'ru') })
                .then(function (j) {
                    setBusy(false);
                    var st = j.__status;
                    if (st === 200 && j.ok && typeof j.lyrics === 'string' && j.lyrics) {
                        $('svoLyrics').value = j.lyrics;
                        // Кнопка «Спеть» гаснет и зажигается по СОБЫТИЮ поля;
                        // подстановка значения из кода его не рождает.
                        $('svoLyrics').dispatchEvent(new Event('input', { bubbles: true }));
                        tell('');
                        panel.hidden = true;
                        openBtn.setAttribute('aria-expanded', 'false');
                        say(T.lg_done || '', 'success');
                        funnel('lyrics_done', {});
                        return;
                    }
                    if (st === 401) { tell(T.need_auth || '', true); return; }
                    if (st === 429) { tell(T.lg_throttled || '', true); funnel('lyrics_failed', { kind: 'throttled' }); return; }
                    if (st === 503) { tell(T.lg_disabled || '', true); funnel('lyrics_failed', { kind: 'disabled' }); return; }
                    if (st === 402) {
                        var need = (typeof j.need === 'number' ? j.need : 0) - (typeof j.balance === 'number' ? j.balance : 0);
                        tell((T.lg_short || '').replace(':n', String(Math.max(0, need))), true);
                        funnel('lyrics_failed', { kind: 'insufficient' });
                        return;
                    }
                    if (st === 422) {
                        tell(j.error === 'blocked' ? (T.lg_blocked || '') : (j.msg || T.lg_failed || ''), true);
                        funnel('lyrics_failed', { kind: j.error === 'blocked' ? 'blocked' : 'validation' });
                        return;
                    }
                    tell(j.msg || T.lg_failed || '', true);
                    funnel('lyrics_failed', { kind: 'other' });
                })
                .catch(function () {
                    setBusy(false);
                    tell(T.lg_failed || '', true);
                    funnel('lyrics_failed', { kind: 'network' });
                });
        });
    })();

    /* ── История прямо в туле ─────────────────────────────────────────────
     *
     * После перезагрузки страницы результат исчезал: строка жила на сервере, а
     * в туле её не было — человек не знал, что песня вообще где-то есть, и
     * узнать мог только случайно, зайдя в «Мои генерации». Для тула, за прогон
     * которого списаны токены, это потеря оплаченного результата на ровном
     * месте.
     *
     * Список берётся ТЕМ ЖЕ маршрутом истории, что у песенного тула, с
     * фильтром «моим голосом», и рисуется ТЕМ ЖЕ renderResult — значит все
     * действия (скачать, обложка, слова, свидетельство, чарт, передача
     * соседям) у прошлых песен ровно те же, что у только что готовой. Второй
     * набор кнопок разошёлся бы с первым молча.
     *
     * Удаление — существующий маршрут библиотеки, полка `song-myvoice`. Своего
     * удаления тул не заводит: одна дверь, один владельческий гейт. */
    var histBefore = null, histBusy = false;

    function histCard(item) {
        var wrap = document.createElement('div');
        wrap.className = 'svo-hist-item';
        renderResult(item, wrap);

        if (!U.hideGen || !item.id) return wrap;
        var del = document.createElement('button');
        del.type = 'button';
        del.className = 'svo-act';
        del.textContent = '🗑️ ' + (T.hist_del || '');
        del.title = T.hist_del_hint || '';
        del.addEventListener('click', function () {
            // Подтверждения НЕТ и не нужно: это не удаление, а перенос —
            // спрашивать «вы уверены?» перед обратимым действием значит учить
            // людей жать «да» не читая.
            if (del._busy) return;
            del._busy = true; del.disabled = true;
            fetch(U.hideGen, {
                method: 'POST', credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrf(),
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({ id: item.id, hidden: 1 })
            }).then(function (r) { return r.ok ? r.json().catch(function () { return { ok: true }; }) : null; })
              .then(function (d) {
                  del._busy = false;
                  if (!d) { del.disabled = false; say(T.err_failed || '', 'error'); return; }
                  wrap.remove();
                  say((d && d.msg) || T.hist_deleted || '', 'success');
                  funnel('history_hide', {});
                  if (!$('svoHistList').children.length) showHistEmpty();
              })
              .catch(function () { del._busy = false; del.disabled = false; say(T.err_failed || '', 'error'); });
        });
        // Кнопка удаления — в ТОТ ЖЕ ряд действий, что и остальные: отдельная
        // строка под карточкой читалась бы как действие над всем списком.
        var acts = wrap.querySelector('.svo-acts');
        if (acts) acts.appendChild(del); else wrap.appendChild(del);
        return wrap;
    }

    function showHistEmpty() {
        var p = document.createElement('p');
        p.className = 'svo-hist-empty';
        p.textContent = T.hist_empty || '';
        $('svoHistList').appendChild(p);
    }

    function loadHistory(more) {
        if (!U.history || histBusy) return;
        var box = $('svoHist'), list = $('svoHistList'), btn = $('svoHistMore');
        if (!box || !list) return;
        histBusy = true;

        var url = U.history + '?tool=song&persona=voice' + (more && histBefore ? '&before=' + histBefore : '');
        get(url).then(function (j) {
            histBusy = false;
            if (!j || !j.ok) return;
            var items = (j.items || []).filter(function (it) {
                // Показываем только ГОТОВОЕ: строка в работе или упавшая — это
                // не песня, а состояние, и ей место не в списке результатов.
                return it.status === 'done' && (it.variants || []).length;
            });
            if (!more) list.innerHTML = '';
            items.forEach(function (it) { list.appendChild(histCard(it)); });
            if (!more && !items.length && !j.has_more) showHistEmpty();
            box.hidden = false;
            histBefore = j.next_before || null;
            if (btn) {
                btn.hidden = !j.has_more;
                if (j.has_more) btn.textContent = (T.hist_more || '').replace(':n', String(Math.max(0, (j.total || 0) - list.querySelectorAll('.svo-hist-item').length)));
            }
        }).catch(function () { histBusy = false; });
    }

    on('svoHistMore', 'click', function () { loadHistory(true); });
    // Свидетельство по файлу: песни в списке уже нет (удалена или ушла по сроку
    // хранения), а скачанный файл у человека остался — сверка по отпечатку.
    on('svoCertByFile', 'click', function () {
        var certS = root.dataset.certStrings;
        if (!window.__trackCertificate || !window.__trackCertificate.byFile || !certS || !U.certMatch) {
            if (window.__toast) window.__toast(T.err_failed || 'error', 'error');
            return;
        }
        funnel('cert_by_file', {});
        window.__trackCertificate.byFile({ matchUrl: U.certMatch, filename: T.cert_file || 'certificate', strings: JSON.parse(certS) });
    });

    function watchGeneration(id) {
        if (!id) return;
        state.activeJobId = id;
        syncRunLocks();
        // Одна дверь наружу: любой терминальный исход снимает замок ровно здесь,
        // поэтому забыть его в новой ветке нельзя — веток с `return` больше нет.
        var endJob = function () {
            if (state.activeJobId !== id) return;   // уже сменился — не трогаем чужой
            state.activeJobId = null;
            syncRunLocks();
        };
        lock('svoResult', false);
        $('svoResultWait').hidden = false;
        $('svoResult').scrollIntoView({ block: 'nearest' });

        // Песня — единственное здесь, за что списаны токены, поэтому у её
        // ожидания не только предел, но и ЧЕСТНЫЙ выход: строка уже создана на
        // сервере, и готовая песня попадёт в «Мои генерации» (раздел «Песни
        // моим голосом») независимо от того, дождалась страница или нет.
        // Сказать это — не утешение, а факт, который спасает оплаченный
        // результат от «я закрыл вкладку и всё пропало».
        var since = Date.now();
        var sawRetry = false;
        var sawNameNote = '';
        (function tick() {
            get(U.status.replace('__ID__', String(id))).then(function (j) {
                var st = j.status || (j.generation && j.generation.status);
                if (st === 'done') {
                    endJob();
                    $('svoResultWait').hidden = true;
                    if ($('svoResultLib')) $('svoResultLib').hidden = false;
                    stage('song_ready');
                    /* Доставка ОБЪЯВЛЯЕТСЯ страницей (config: output_kind server):
                       у этого лейна объявления не было, и каждая готовая песня
                       уезжала в телеметрию как `abandon_no_complete` — 84
                       song_ready за 30 дней при 0 tool_complete с этой ветки. */
                    try {
                        if (window.__aeToolComplete) window.__aeToolComplete({ lane: 'suno', gen: id, variants: (j.variants || (j.generation && j.generation.variants) || []).length });
                    } catch (e) {}
                    renderResult(j);
                    // Имя, заменённое описанием, человек узнаёт и тогда, когда
                    // замена случилась между двумя опросами.
                    if (j.note) say(String(j.note), 'info');
                    loadBalance();
                    // Свежая песня должна оказаться и в списке прошлых — иначе
                    // после первой же перезагрузки она «исчезнет».
                    loadHistory(false);
                    return;
                }
                if (st === 'failed') {
                    endJob();
                    loadBalance();
                    $('svoResultWait').hidden = true;
                    if ($('svoResultLib')) $('svoResultLib').hidden = false;
                    /* Сервер УЖЕ называет причину человеческими словами —
                       `msg` из friendlyFailureMsg() (совпадение с каталогом,
                       копирайт, запрещённые слова). Эта ветка её выбрасывала и
                       показывала общее «Не получилось», хотя все остальные
                       вызовы в этом файле пишут `j.msg || …`. Сосед по движку
                       (/song-generator) читает msg с самого начала — сюда это
                       правило просто не доехало.
                       Живой случай: NE_NAGIEV, 23.08 21:33, генерация #1367 —
                       Suno отказал «совпадает с записью в каталоге», а человек
                       прочитал «попробуйте ещё раз». Пробовать было нечего:
                       тот же исходник отказали бы снова.
                       ⚠️ Сырой `j.error` не показываем никогда — это
                       техническая английская строка (политика та же, что в
                       /song-generator). */
                    /* Отказ по каталогу на ЭТОЙ вкладке непреодолим: тот же файл
                       отвергнут снова. Соседний лейн — наш SVC, он каталог не
                       сверяет. Решаем по `msg_kind` (ключ сервера), а не по
                       переведённой фразе. */
                    var fileRefused = { fail_catalog: 1, fail_audio_lyrics: 1 }[j.msg_kind] === 1
                        && j.operation === 'cover';
                    var laneOut = fileRefused && !!$('svoErrLane') && !!coverFile;
                    if (laneOut) laneFile = coverFile;
                    if (fileRefused) showErr(j.msg || T.err_failed || '', { lane: laneOut });
                    else say(j.msg || T.err_failed || '', 'error');
                    funnel('song_failed', {
                        named: j.msg ? 1 : 0,
                        refunded: j.refunded ? 1 : 0,
                        lane_out: laneOut ? 1 : 0,
                        code: String(j.error || '').slice(0, 40)
                    });
                    return;
                }
                /* Провайдер сорвался, бэкенд молча запустил новый рендер —
                   скажи об этом. Страница не читала `phase` вообще, поэтому всё
                   ожидание (а с лестницей повторов оно длиннее) шло под одной
                   неизменной строкой «Готовим — обычно 2–3 минуты»: человек
                   видел ровно то же, что и в первые десять секунд, и решал, что
                   всё висит. Сосед по движку (/song-generator) говорит это с
                   тех пор, как повтор появился; сюда правило не доехало. */
                if (String(j.phase || '').toUpperCase() === 'RETRY') {
                    if ($('svoResultWait')) {
                        $('svoResultWait').hidden = false;
                        $('svoResultWait').textContent = T.song_retry || T.sv_result_wait || $('svoResultWait').textContent;
                    }
                    if (!sawRetry) { sawRetry = true; funnel('song_retry', {}); }
                }
                /* Провайдер не принял имя исполнителя в стиле, сервер заменил его
                   описанием звучания и отправил снова (`note` — его слова, на
                   языке читателя). Ожидание стало длиннее — сказать почему. */
                if (j.note && !sawNameNote) {
                    sawNameNote = String(j.note);
                    if ($('svoResultWait')) {
                        $('svoResultWait').hidden = false;
                        $('svoResultWait').textContent = sawNameNote;
                    }
                    funnel('song_name_repair', {});
                }
                if (Date.now() - since > WAIT_SONG_MS) {
                    endJob();
                    $('svoResultWait').hidden = true;
                    if ($('svoResultLib')) $('svoResultLib').hidden = false;
                    funnel('song_wait_timeout', {});
                    say(T.song_slow || T.err_failed || '', 'warning');
                    return;
                }
                setTimeout(tick, 4000);
            }).catch(function () {
                if (Date.now() - since > WAIT_SONG_MS) {
                    endJob();
                    $('svoResultWait').hidden = true;
                    if ($('svoResultLib')) $('svoResultLib').hidden = false;
                    say(T.song_slow || T.err_failed || '', 'warning');
                    return;
                }
                setTimeout(tick, 6000);
            });
        })();
    }

    /* Готовая песня — это ПЕСНЯ, а не только плеер.
     *
     * Голосовой лейн пишет те же строки, что генератор песен (`tool=song`,
     * sunoapi, тот же `variantPayload()`), поэтому здесь не новая механика, а
     * подключение уже существующей: скачивание, обложка, слова, свидетельство
     * (общий примитив `window.__trackCertificate`) и чарт (тот же маршрут).
     * Раньше человек получал голый <audio> и не мог ни сохранить обложку, ни
     * забрать слова, ни получить права, ни опубликовать — при том что данные
     * для всего этого уже лежали в ответе.
     *
     * ⚠️ И сам плеер играл через `attachment`-ветку: без `inline=1` сервер
     * отдаёт вложение целиком, БЕЗ поддержки Range, то есть перемотки в
     * результате не было вообще. */
    function qsuf(u, q) { return u + (u.indexOf('?') >= 0 ? '&' : '?') + q; }

    function dlBase(title) {
        var t = String(title || T.result_song || 'song')
            .replace(/[\\\/:*?"<>|]/g, " ").replace(/\s+/g, " ").trim();
        return t.slice(0, 60) || 'song';
    }

    /* WAV — пережатие В БРАУЗЕРЕ: серверного WAV у нас нет, провайдер отдаёт
     * mp3. Кодировщик берём ОБЩИЙ (`AudioPipeline.audioBufferToWav`) и грузим
     * по требованию: 41 КБ ради одной кнопки платят только те, кто её нажал.
     * Третья копия писателя WAV в блейде была бы багом, а не экономией. */
    var pipelineReady = null;
    function loadPipeline() {
        if (window.AudioPipeline && window.AudioPipeline.audioBufferToWav) return Promise.resolve(true);
        if (!U.pipeline) return Promise.resolve(false);
        if (pipelineReady) return pipelineReady;
        pipelineReady = new Promise(function (res) {
            var sc = document.createElement('script');
            sc.src = U.pipeline;
            sc.onload = function () { res(!!(window.AudioPipeline && window.AudioPipeline.audioBufferToWav)); };
            sc.onerror = function () { res(false); };
            document.head.appendChild(sc);
        });
        return pipelineReady;
    }

    function exportWav(url, name, btn) {
        btn.disabled = true;
        var free = function () { btn.disabled = false; };
        loadPipeline().then(function (okp) {
            if (!okp) throw new Error('pipeline');
            return fetch(qsuf(url, 'inline=1'), { credentials: 'same-origin' });
        }).then(function (r) {
            if (!r.ok) throw new Error('http ' + r.status);
            return r.arrayBuffer();
        }).then(function (ab) {
            var AC = window.AudioContext || window.webkitAudioContext;
            var ctx = new AC();
            return ctx.decodeAudioData(ab).then(function (buf) {
                try { ctx.close(); } catch (e) {}
                return buf;
            });
        }).then(function (buf) {
            var blob = window.AudioPipeline.audioBufferToWav(buf);
            var send = function (b) {
                if (window.__downloadFile) window.__downloadFile(b, name);
                say(T.dl_started || '', 'success');
                free();
            };
            // Пережатие в WAV теряет серверные ID3-метки «создано ИИ» — ставим
            // их заново на границе выдачи (243-ФЗ ст. 9 / EU AI Act 50(2)).
            if (window.__aiMark) window.__aiMark.auto(blob, name, 'voice-cover').then(send, function () { send(blob); });
            else send(blob);
            funnel('download_wav', {});
        }).catch(function () {
            free();
            say(T.dl_failed || T.err_failed || '', 'error');
        });
    }

    function actLink(href, text, dl) {
        var a = document.createElement('a');
        a.className = 'svo-act'; a.href = href; a.textContent = text;
        if (dl) a.setAttribute('download', dl);
        return a;
    }

    function actBtn(text, onClick) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'svo-act'; b.textContent = text;
        b.addEventListener('click', onClick);
        return b;
    }

    function renderResult(j, into) {
        var list = (j.variants || (j.generation && j.generation.variants) || []);
        var title = (j.title || (j.generation && j.generation.title) || '');
        var box = into || $('svoResultList');
        if (!into) box.innerHTML = '';

        list.forEach(function (v, i) {
            var n = v.n || (i + 1);
            var vn = n === 1 ? 'A' : (n === 2 ? 'B' : String(n));
            var url = v.url || v.path_url || v.src || '';
            var ext = v.ext || 'mp3';
            var base = dlBase(title);
            var gen = v.id || j.id || (j.generation && j.generation.id) || null;

            var wrap = document.createElement('div');
            wrap.className = 'svo-res';

            // Шапка карточки: обложка + название + длительность. До этого
            // здесь стояло одно слово «Вариант N» — результат, за который
            // списаны токены, выглядел как безымянный файл.
            var head = document.createElement('div');
            head.className = 'svo-res-head';

            if (v.cover_url) {
                var img = document.createElement('img');
                img.className = 'svo-res-cover';
                img.src = v.cover_url; img.alt = ''; img.loading = 'lazy';
                // Обложка живёт ровно столько же, сколько сам файл, и после
                // истечения срока хранения отдаёт 410. Битая картинка оставила
                // бы в шапке пустой квадрат и сдвинутое название — убираем её.
                img.addEventListener('error', function () { img.remove(); });
                head.appendChild(img);
            }

            var meta = document.createElement('div');
            meta.className = 'svo-res-meta';
            var h = document.createElement('span');
            h.className = 'svo-res-title';
            // У разделённого трека дорожки — не альтернативы, а части: «Вокал» и
            // «Минус» говорят больше, чем «Вариант 1» и «Вариант 2».
            var kindName = v.kind ? (T['stem_' + v.kind] || v.kind) : '';
            h.textContent = (title || (T.result_song || '')) + ' · '
                + (kindName || ((T.variant || '') + ' ' + n));
            meta.appendChild(h);
            if (v.duration_sec) {
                var d = document.createElement('span');
                d.className = 'svo-res-dur';
                var sec = Math.round(v.duration_sec);
                d.textContent = Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0');
                meta.appendChild(d);
            }
            head.appendChild(meta);

            var a = document.createElement('audio');
            a.controls = true; a.preload = 'none';
            if (url) a.src = qsuf(url, 'inline=1');

            wrap.appendChild(head);
            wrap.appendChild(a);

            var acts = document.createElement('div');
            acts.className = 'svo-acts';

            // Прямая ссылка на сервер, а не blob: на Android/MIUI blob молча
            // не сохраняется (тот же вывод, что у песенного тула).
            if (url) {
                // Главное действие среди одиннадцати обязано быть видно: без
                // выделения «Скачать MP3» теряется в ряду одинаковых кнопок.
                var mp3 = actLink(url, T.download_mp3 || 'MP3', base + ' (' + vn + ').' + ext);
                mp3.classList.add('svo-act-primary');
                acts.appendChild(mp3);
            }

            // WAV рядом с MP3 — из МАСТЕРА провайдера на нашем диске, тем же лейном,
            // что у генератора песен и «Моих генераций» (master-download.js):
            // без второго поколения потерь, с докачкой при обрыве, и сервер
            // помнит отпечаток выданного файла — свидетельство о создании его
            // опишет. До 30.09.2026 WAV собирался здесь, в браузере, из MP3:
            // этих байт сервер не видел, и описать их было нечем (чат 658).
            // Браузерная сборка осталась запасным путём — мастера нет (старые
            // треки эпохи genapi) или примитив не загрузился.
            if (url && ext !== 'wav' && (window.__masterDownload || U.pipeline)) {
                acts.appendChild(actBtn(T.download_wav || 'WAV', function (e) {
                    var btn = e.currentTarget, name = base + ' (' + vn + ').wav';
                    if (!window.__masterDownload) { exportWav(url, name, btn); return; }
                    window.__masterDownload(btn, {
                        url: url, query: 'master=wav', name: name,
                        labels: { preparing: T.master_preparing || '', failed: T.master_failed || T.dl_failed || '',
                                  started: T.dl_started || '', mb: T.mb || 'MB' },
                        onDone: function (bytes) { funnel('download_wav', { src: 'master', bytes: bytes }); },
                        onFail: function () { if (U.pipeline) exportWav(url, name, btn); }
                    });
                }));
            }

            if (v.cover_url) acts.appendChild(actLink(qsuf(v.cover_url, 'dl=1'),
                T.download_cover || 'Cover', base + ' cover.jpg'));

            if (v.has_lyrics && gen && U.lyricsBase) {
                acts.appendChild(actLink(
                    U.lyricsBase + '/' + encodeURIComponent(gen) + '/' + encodeURIComponent(n) + '?format=txt',
                    T.lyr_txt || 'TXT', base + ' (' + vn + ').txt'));
            }

            // Свидетельство — тот же общий модуль, что у генератора песен. Какой
            // файл описать (MP3, WAV…) человек выбирает в его диалоге; сервер
            // помнит отпечаток каждого выданного файла.
            var certS = root.dataset.certStrings;
            if (gen && url && certS && U.cert && U.certMatch) {
                acts.appendChild(actBtn(T.cert_btn || 'PDF', function (e) {
                    var btn = e.currentTarget;
                    if (btn._busy) return;
                    if (!window.__trackCertificate) {
                        if (window.__toast) window.__toast(T.err_failed || 'error', 'error');
                        return;
                    }
                    btn._busy = true; btn.disabled = true;
                    var done = function () { btn._busy = false; btn.disabled = false; };
                    window.__trackCertificate({
                        dataUrl: U.cert + '/' + encodeURIComponent(gen) + '/' + encodeURIComponent(n),
                        matchUrl: U.certMatch,
                        idLabel: gen + '-' + n,
                        filename: base + ' — ' + (T.cert_file || 'certificate'),
                        strings: JSON.parse(certS)
                    }).then(done, done);
                }));
            }

            // Чарт — только там, где он есть (RU).
            if (gen && root.dataset.chartUrl) {
                acts.appendChild(actBtn('★ ' + (root.dataset.chartBtn || 'Chart'), function (e) {
                    var btn = e.currentTarget;
                    if (btn._busy) return;
                    btn._busy = true; btn.disabled = true;
                    fetch(root.dataset.chartUrl, {
                        method: 'POST', credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json', 'Accept': 'application/json',
                            'X-CSRF-TOKEN': (document.querySelector('meta[name=csrf-token]') || {}).content || ''
                        },
                        body: JSON.stringify({ generation_id: gen, variant: n })
                    }).then(function (r) {
                        return r.json().catch(function () { return {}; });
                    }).then(function (d) {
                        btn._busy = false;
                        if (d && d.ok) {
                            btn.textContent = '✓'; btn.disabled = true;
                            if (window.__toast && root.dataset.chartHint) window.__toast(root.dataset.chartHint, 'success');
                        } else {
                            btn.disabled = false;
                            if (window.__toast) window.__toast((d && d.msg) || (T.err_failed || 'error'), 'error');
                        }
                    }).catch(function () {
                        btn._busy = false; btn.disabled = false;
                        if (window.__toast) window.__toast(T.err_failed || 'error', 'error');
                    });
                }));
            }

            // Караоке и субтитры. В отличие от TXT это НЕ прямая ссылка: первый
            // запрос покупает у провайдера потактовые тайминги, идёт секунды и
            // может законно не получиться — значит нужен занятой вид и честный
            // отказ, а не ссылка, вываливающая JSON в новую вкладку.
            if (v.lyrics_sync && gen && U.lyricsBase) {
                ['lrc', 'srt'].forEach(function (fmt) {
                    acts.appendChild(actBtn(T['lyr_' + fmt] || fmt.toUpperCase(), function (e) {
                        var btn = e.currentTarget;
                        if (btn._busy) return;
                        btn._busy = true; btn.disabled = true;
                        var free = function () { btn._busy = false; btn.disabled = false; };
                        var name = base + ' (' + vn + ').' + fmt;
                        fetch(U.lyricsBase + '/' + encodeURIComponent(gen) + '/' + encodeURIComponent(n) + '?format=' + fmt,
                              { credentials: 'same-origin', headers: { Accept: 'text/plain' } })
                            .then(function (r) {
                                if (r.status === 401 || r.status === 419) { if (U.login) window.location.href = U.login; throw new Error('auth'); }
                                if (r.status === 429) throw new Error('throttled');
                                if (!r.ok) throw new Error('unavailable');
                                return r.text();
                            })
                            .then(function (txt) {
                                if (!txt || !txt.trim()) throw new Error('unavailable');
                                var blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
                                if (window.__downloadFile) window.__downloadFile(blob, name);
                                say(T.dl_started || '', 'success');
                                funnel('download_lyrics', { format: fmt });
                                free();
                            })
                            .catch(function (err) {
                                free();
                                if (String(err && err.message) === 'auth') return;
                                say(String(err && err.message) === 'throttled'
                                    ? (T.lg_throttled || '') : (T.lyr_err_sync || T.err_failed || ''), 'warning');
                            });
                    }));
                });
            }

            // Передать песню соседнему тулу НЕ скачивая: едет URL и имя, файл
            // остаётся у нас — принимающая страница забирает его сама тем же
            // сеансом. Раньше человек качал файл и загружал его обратно.
            Object.keys(U.goTo || {}).forEach(function (key) {
                var d = U.goTo[key];
                if (!d || !d.url || !url) return;
                var a = actLink(d.url, d.label || key, null);
                a.addEventListener('click', function () {
                    try {
                        // Имя песни едет рядом с файлом: /audio-to-video подписывает
                        // им клип, а не тегами MP3 (там буква дубля, чат 592).
                        // …и слова песни: /audio-to-video ставит их на голос сразу (#1977).
                        var words = (v.has_lyrics && gen && U.lyricsBase && !v.kind)
                            ? U.lyricsBase + '/' + encodeURIComponent(gen) + '/' + encodeURIComponent(n) : '';
                        sessionStorage.setItem(d.carry, JSON.stringify({
                            url: url, name: base + '-v' + n + '.' + ext,
                            title: v.kind ? '' : String(title || ''),
                            lyrics: words, lyricsSync: !!(words && v.lyrics_sync), createdAt: Date.now()
                        }));
                    } catch (e) { /* квота/выключено — тул просто откроется пустым */ }
                    funnel('handoff_' + key, {});
                });
                acts.appendChild(a);
            });

            // «Стемы» — ОДНА на песню, у первой дорожки: разделение берёт трек
            // целиком, и кнопка под каждым вариантом обещала бы выбор, которого
            // нет. У самих стемов её нет — делить уже разделённое нечего.
            if (i === 0 && gen && !v.kind && U.action) {
                acts.appendChild(actBtn('🎚️ ' + (T.stems_btn || ''), function (e) {
                    var btn = e.currentTarget;
                    if (btn._busy) return;
                    btn._busy = true; btn.disabled = true;
                    say(T.stems_started || '', 'info');
                    post(U.action, { operation: 'stems', parent_id: gen, source_variant: n })
                        .then(function (j) {
                            btn._busy = false;
                            if (j.__status === 200 && j.ok && j.id) {
                                funnel('stems_started', {});
                                // Разделение — НОВАЯ генерация: ждём её тем же
                                // опросом, что и песню, и показываем на месте.
                                watchGeneration(j.id);
                                return;
                            }
                            btn.disabled = false;
                            if (j.__status === 402) { say(T.stems_short || '', 'warning'); return; }
                            say(j.msg || T.err_failed || '', 'error');
                        })
                        .catch(function () {
                            btn._busy = false; btn.disabled = false;
                            say(T.err_failed || '', 'error');
                        });
                }));
            }

            if (acts.children.length) wrap.appendChild(acts);
            box.appendChild(wrap);
        });
    }

    // ── helpers ─────────────────────────────────────────────────────────────
    function num(v) { var n = parseFloat(v); return isNaN(n) ? null : n; }

    /* Читать поле по id, а не через `$('x').value`. Разница не косметическая:
     * одна отсутствующая ручка роняла бы ВЕСЬ отправляющий обработчик — ровно
     * тот способ умереть, против которого заведён `on()` в шапке файла. */
    function val(id, fallback) {
        var el = $(id);
        return el && el.value !== undefined && el.value !== '' ? el.value : fallback;
    }
    function checked(id) {
        var el = $(id);
        return !!(el && el.checked);
    }
    /* «Только мой голос» помнится, как выбор человека: снятая галочка остаётся
       снятой при следующем визите. Привязка — после разбора страницы: layout
       объявляет __toolSettings ниже контента. */
    function bindSolo() {
        var TS = window.__toolSettings;
        if (!TS) return;
        if ($('svoSolo')) TS.bind('voice-cover', $('svoSolo'), 'solo', true);
        if ($('svoCoverSolo')) TS.bind('voice-cover', $('svoCoverSolo'), 'solo_cover', true);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindSolo);
    else bindSolo();
    function numOr(id, fallback) {
        var el = $(id), n = el ? parseFloat(el.value) : NaN;
        return isNaN(n) ? fallback : n;
    }

    function fmt(sec) {
        var s = Math.max(0, Math.round(sec));
        return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
    }

    // Live readout for the three weight sliders — a bare slider with no number
    // is a guess, and these change the result audibly.
    ['svoStyleW', 'svoWeird', 'svoAudioW'].forEach(function (id) {
        var el = $(id); if (!el) return;
        var out = $(id + 'Out');
        var sync = function () { if (out) out.textContent = el.value; };
        el.addEventListener('input', sync);
        sync();
    });

    reportMissing('init');
    // Гостю — объяснение под кнопкой сразу, а не после сетевого ответа.
    if (state.auth !== undefined) applyGate(state.auth, state.verified);
    loadInfo();
})();
